import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Lock, CreditCard, ShieldCheck, Crown, Info, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { TESTS } from "@/lib/siteData";
import { pushFormEvent } from "@/lib/analytics";
import { useLivePricing } from "@/lib/useLivePricing";
import { usePublicPricing } from "@/lib/usePublicPricing";
import { BOOKING } from "@/constants/testIds";
import { BrandLogo } from "@/components/BrandLogo";
import { BackToPortfolioButton } from "@/components/BackToPortfolioButton";
import { useAuth } from "@/lib/auth";
import { PropertyIntakeForm, intakeFormToPayload, validateIntakeForm } from "@/components/PropertyIntakeForm";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { PassportAddonCard } from "@/components/PassportAddonCard";
import { ZoomableImage } from "@/components/ZoomableImage";
import {
  readPartnerAttribution,
  writePartnerAttribution,
  clearPartnerAttribution,
  captureUTMFromURLSearchParams,
} from "@/lib/partnerAttribution";
import { trackEvent } from "@/lib/pwsTracking";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import { useFeature } from "@/lib/useFeature";
// Aware moved to a national model (Feb 2026) — the "Check we service this
// address" widget and its excluded-suburb verdict banner were removed
// from the customer booking flow. `parseAddress` is still used below to
// extract a postcode for the (internal-only) PWS partner sample-
// collection availability lookup — that display never blocks a booking.
import { parseAddress, parseAusAddress } from "@/lib/serviceArea";

// Phase 2J-AJ — AWARE Water Health Record booking-flow add-on price in cents.
// Single source of truth lives on the backend (`core.constants.PASSPORT_BOOKING_ADDON_CENTS`);
// the server re-validates this value at checkout so the client is never trusted.
const PASSPORT_ADDON_CENTS = 4950;

const TEST_TESTID = {
  basic: BOOKING.testCardBasic,
  tank: BOOKING.testCardTank,
  bore: BOOKING.testCardBore,
};

// Phase 2L-A39 — `REASON_FOR_TESTING_OPTIONS` / `PRIMARY_WATER_USE_OPTIONS`
// were removed from the customer-facing booking form. The constants
// still live in `CreateBookingModal.jsx` for admin-created bookings.
// Server-side `CustomerDetails.reason_for_testing` /
// `primary_water_use` remain Optional so legacy records and admin
// views keep working unchanged.

const STEPS = [
  { n: 1, label: "Choose Test" },
  { n: 2, label: "Your Details" },
  { n: 3, label: "Payment" },
];

export default function Booking() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Feb 2026 — Central Online Discount switch. Every "Save X%" / pill
  // / strike-through on this page conditions on `discountEnabled` and
  // `discountPct` so the admin toggle collapses the booking flow to
  // clean single-price retail wording when disabled. Never touches
  // PM/partner/PWS-attributed pricing — those flows have their own
  // hasStrike/discount handling downstream.
  const {
    enabled:           discountEnabled,
    pct:               discountPct,
    showStrikethrough,
    savingsText:       discountSavingsText,
    pill:              discountPill,
  } = usePublicPricing();

  // Feb 2026 — SSoT price fetch. Overrides bundled prices in
  // siteData.TESTS with the current admin All-Inclusive tier so
  // Prod price edits reach the display without a code push.
  const livePricedTests = useLivePricing();

  useSEO({
    title: "Book a Water Test Online | Aware Water Testing",
    description: discountEnabled && discountPct > 0
      ? `Book a tank or bore water test online with Aware Water Testing. Pre-paid online booking saves ${discountPct}% — drop your sample to our Noosaville laboratory.`
      : "Book a tank or bore water test online with Aware Water Testing. Drop your sample to our Noosaville laboratory.",
    url: `${SEO_DEFAULTS.baseUrl}/book-water-test`,
  });
  const initialTest = searchParams.get("test") || "tank";
  const prefillPropertyId = searchParams.get("property");
  // Phase 2L-A80 — Invite-a-Neighbour voucher. The invitee email
  // carries `?invite=<secure_token>`; we look it up on mount, show a
  // banner, and forward the token to the checkout endpoint. Server
  // re-validates and atomically claims the voucher at checkout
  // creation time so a tampered payload can't double-apply.
  const initialInviteToken = searchParams.get("invite") || "";
  const [inviteToken, setInviteToken] = useState(initialInviteToken);
  // Jun 2026 — In-Field Technician Booking (Property Campaigns). The tech
  // arrives via /book-water-test?field=1 straight from the completed
  // inspection; the prefill blob (customer + instant 20% code) was written
  // to localStorage by FieldInspection just before navigation. 2h expiry so
  // a stale blob never leaks into a later normal booking.
  const [fieldBooking] = useState(() => {
    if (searchParams.get("field") !== "1") return null;
    try {
      const blob = JSON.parse(localStorage.getItem("aware_field_booking") || "null");
      if (!blob?.code || (Date.now() - (blob.ts || 0)) > 2 * 60 * 60 * 1000) return null;
      return blob;
    } catch { return null; }
  });
  const [voucher, setVoucher] = useState(null); // {valid,voucher_code,amount,expires_at}
  const [voucherErr, setVoucherErr] = useState("");

  // Phase 2 (Aware Partner Network) — partner-link attribution.
  // The customer landed on /book-water-test?p=PWS-NOOSA (etc). We resolve
  // the slug to an ACTIVE PWS franchisee, show a "Booking via X" banner,
  // and forward the slug to the checkout endpoint. The server re-resolves
  // server-side; only active partners get the booking attribution stamp.
  // A draft / inactive / suspended / unknown slug silently falls back to
  // the normal anonymous flow (no banner, no attribution, no error).
  //
  // Phase 3 — 90-day persistence: when a fresh `?p=` click resolves, we
  // write a `pws_partner_attribution` blob to localStorage. When the
  // customer returns later through the bare /book-water-test URL we
  // read the blob and forward it to /payments/checkout; the server
  // re-validates expiry + ACTIVE partner.
  const initialPartnerSlug = searchParams.get("p") || "";
  const [partnerSlug] = useState(initialPartnerSlug);
  const [linkedPartner, setLinkedPartner] = useState(null);
  const [storedAttribution, setStoredAttribution] = useState(null);
  const [attributionFromStorage, setAttributionFromStorage] = useState(false);
  // Phase 30 — Optional Local Sample Collection. Display-only;
  // never schedules, never charges, never sends notifications.
  const [sampleCollection, setSampleCollection] = useState(null);

  useEffect(() => {
    // Path A — fresh ?p= click: resolve the slug, write the blob,
    // show the banner.
    if (partnerSlug) {
      let cancelled = false;
      api.get(`/partners/resolve-slug/${encodeURIComponent(partnerSlug)}`)
        .then(({ data }) => {
          if (cancelled) return;
          setLinkedPartner(data);
          setAttributionFromStorage(false);
          // Phase 27 — partner-link click event (fail-soft).
          trackEvent("partner_link_click", {
            partnerSlug: data.partner_slug,
            attributionSource: "partner_link",
          });
          try {
            const utm = captureUTMFromURLSearchParams(searchParams);
            writePartnerAttribution({
              partner_slug: data.partner_slug,
              partner_id: data.id,
              source_url: typeof window !== "undefined" ? window.location.href : null,
              utm,
              source: searchParams.get("src"),
              campaign: searchParams.get("cmp"),
            });
          } catch { /* localStorage may be unavailable in privacy mode */ }
        })
        .catch(() => { /* non-active or unknown slug; no banner */ });
      return () => { cancelled = true; };
    }
    // Path B — no `?p=` in the URL: try to honour a previously stored
    // 90-day attribution blob (Phase 3).
    const blob = readPartnerAttribution();
    if (!blob) return;
    let cancelled = false;
    api.get(`/partners/resolve-slug/${encodeURIComponent(blob.partner_slug)}`)
      .then(({ data }) => {
        if (cancelled) return;
        setLinkedPartner(data);
        setStoredAttribution(blob);
        setAttributionFromStorage(true);
      })
      .catch(() => {
        // Stored slug no longer resolves (partner paused / removed) →
        // drop the blob silently and continue with anonymous flow.
        clearPartnerAttribution();
      });
    return () => { cancelled = true; };
  }, [partnerSlug]);

  const [step, setStep] = useState(1);
  const [selectedTest, setSelectedTest] = useState(initialTest);

  // Feb 2026 — Booking Availability Control. `availability[test_id]`
  // is `{available, customer_message, available_again_date}`.
  // Fetched once on mount from the PUBLIC status endpoint (no auth).
  // Fail-soft: if the call fails we default every package to
  // `{available: true}` so a transient outage never blocks legitimate
  // customers. The server ALWAYS re-checks at checkout so this is
  // just UX; the guardrail is on `/api/payments/checkout`.
  const [availability, setAvailability] = useState({});
  useEffect(() => {
    let cancelled = false;
    api.get("/booking-availability/status")
      .then(({ data }) => {
        if (cancelled) return;
        setAvailability(data?.packages || {});
      })
      .catch(() => { if (!cancelled) setAvailability({}); });
    return () => { cancelled = true; };
  }, []);

  // If the URL preselected a disabled package (`/book?package=tank`
  // while tank is unavailable), clear the selection so the customer
  // isn't pushed into a dead-end flow. Preserves the greyed-out card
  // so they can still see the message + reason.
  useEffect(() => {
    if (!selectedTest) return;
    const st = availability[selectedTest];
    if (st && st.available === false) setSelectedTest("");
  }, [selectedTest, availability]);

  // Phase 27 — booking_started (once per session) + booking_step_viewed
  // (every step change). Fail-soft; never blocks the flow.
  const bookingStartedFired = useRef(false);
  useEffect(() => {
    if (bookingStartedFired.current) return;
    bookingStartedFired.current = true;
    trackEvent("booking_started", {
      partnerSlug: partnerSlug || null,
      testId: initialTest || null,
      attributionSource: partnerSlug
        ? "partner_link"
        : (storedAttribution ? "stored_partner_attribution" : "direct_scwt"),
    });
  }, [partnerSlug, initialTest, storedAttribution]);

  useEffect(() => {
    trackEvent("booking_step_viewed", {
      partnerSlug: partnerSlug || null,
      testId: (selectedTest && selectedTest.id) || null,
      currentStep: step,
      attributionSource: partnerSlug
        ? "partner_link"
        : (storedAttribution ? "stored_partner_attribution" : "direct_scwt"),
    });
  }, [step, partnerSlug, selectedTest, storedAttribution]);
  // expert_advice add-on was removed Feb 2026 — customers receive the
  // plain-English Summary Report + Recommended Actions free of charge, and
  // can request a free Pristine Water Systems proposal/assessment if
  // corrective work is recommended. Kept as a const `false` so historical
  // payload shapes remain compatible across the booking API.
  const expertAdvice = false;
  const [details, setDetails] = useState({
    name: "", mobile: "", email: "", address: "", sampleType: "",
    reason_for_testing: "", reason_for_testing_other: "",
    primary_water_use: "", primary_water_use_other: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [rebookInfo, setRebookInfo] = useState(null);
  const [membership, setMembership] = useState(null);
  const [intake, setIntake] = useState(null);
  // Phase 25 — Customer Consent Wording. The customer's explicit
  // acknowledgement of the partner-support disclosure shown on Step 3.
  // Forwarded to /payments/checkout where the server stamps the
  // canonical disclosure text + version + source on the new booking.
  // None / false do NOT block checkout — the disclosure is still
  // persisted, just with a `not_captured` flag.
  const [partnerSupportConsent, setPartnerSupportConsent] = useState(false);
  // Phase 2L-A3 — discount code state.
  const [discountInput, setDiscountInput] = useState("");
  const [discount, setDiscount] = useState(null);  // {code, type, value, amount, final_total}
  // Jun 2026 — Landlord Water Health member discount preview state.
  const [lwhDiscount, setLwhDiscount] = useState(null);
  const [discountErr, setDiscountErr] = useState("");
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  // Phase 2J-AJ — AWARE Water Health Record booking add-on (50% off).
  // Hidden for existing members; server re-validates on checkout.
  // Feb 2026 — Passport add-on retired. State kept only so historical
  // summary references still resolve; always false, no UI to change it.
  const [passportAddon] = useState(false);
  // Phase 2L-A31 — optional sample-identification fields. Surfaced on
  // labels, COA, summary and lab queue search so multi-sample customers
  // can tell their tests apart.
  const [customerSampleReference, setCustomerSampleReference] = useState("");
  const [samplePoint, setSamplePoint] = useState("");
  // Phase 8 — Sample kit method. Default to `local_pickup_dropoff`
  // (no fee) so posted-kit fulfilment is strictly OPT-IN. Feb-2026
  // Feb 2026 — default to NO selection so the fee is not silently added
  // to the Order Summary before the customer explicitly picks an option.
  // `validateStep2` blocks the "Continue" button when the kit-source
  // picker is visible AND no method has been chosen. Auto-selection
  // still happens (further down) when only ONE option is visible — in
  // that case the picker itself is hidden so we skip straight through.
  const [kitMethod, setKitMethod] = useState("");

  // Feb-2026 bugfix — postal-address source toggle. "property" reuses
  // the sample/property address already entered on Step 2 so the
  // customer doesn't retype; "custom" reveals a Google-autocomplete
  // input + editable fields. Default "property" because that's the
  // 90% case and matches the customer's mental model.
  const [postalAddressSource, setPostalAddressSource] = useState("property");

  // Feb-2026 bugfix — when the customer types into the postal
  // AddressAutocomplete (or picks a Google Places suggestion), we get
  // back a single free-form string. Split it into structured fields
  // so the customer doesn't have to retype suburb / state / postcode.
  // Only auto-fills components we could actually infer; leaves the
  // rest untouched so a manual edit isn't clobbered.
  //
  // Nov 2026 (SCWT-34E6D6 follow-up) — line1 is deliberately left as
  // the raw typed string here (every keystroke) so the visible input
  // always mirrors exactly what the customer is typing — overwriting
  // it mid-keystroke with a regex-derived "street only" guess cuts off
  // further typing. The street-only split for line1 happens
  // separately, on blur (`splitPostedKitLine1OnBlur` below) or on an
  // actual Google suggestion pick (`onPlace`), never on every keystroke.
  const setPostalAddressAddressFromAutocomplete = (raw, setter) => {
    const parsed = parseAusAddress(raw || "");
    setter((prev) => ({
      line1:    raw || "",
      line2:    prev.line2 || "",
      suburb:   parsed.suburb   || prev.suburb   || "",
      state:    parsed.state    || prev.state    || "",
      postcode: parsed.postcode || prev.postcode || "",
      country:  parsed.country  || prev.country  || "Australia",
    }));
  };

  // Nov 2026 (SCWT-34E6D6 follow-up) — once the customer leaves the
  // "custom" postal address field (blur), clean up line1 so it holds
  // just the street — not the whole typed string with suburb/postcode
  // baked back in. Safe to run on blur (typing has already finished).
  const splitPostedKitLine1OnBlur = () => {
    setPostedKitAddress((prev) => {
      const parsed = parseAusAddress(prev.line1 || "");
      if (!parsed.line1) return prev;
      return { ...prev, line1: parsed.line1 };
    });
  };

  // ── Phase 2L-A144 — Paid posted sample kit (Feb 2026) ──────────────
  // Fetched once on mount from `/api/public/posted-kit-settings`. When
  // `postedKit.enabled === false` we render the LEGACY 3-option kit
  // picker (public sees zero change). When enabled, we render the new
  // 2-option lab-pickup-or-post layout with the configurable fee +
  // admin-uploaded kit image + reveal-on-select postal address form.
  const [postedKit, setPostedKit] = useState({ enabled: false });
  const [postedKitAddress, setPostedKitAddress] = useState({
    line1: "", line2: "", suburb: "", state: "", postcode: "", country: "Australia",
  });
  const [postedKitDeliveryNotes, setPostedKitDeliveryNotes] = useState("");
  // Feb 2026 — Bulk Kit customer picker. When the linked partner is
  // ACTIVE and holds ≥1 kit, we surface a 4th kit-method option
  // "Kit supplied by my PWS Partner — $34". Server re-validates on
  // /payments/checkout — the frontend value is UX only.
  const [partnerKitOptions, setPartnerKitOptions] = useState({
    available: false, partner: null, customer_charge_cents: 3400, reason: "no_attribution",
  });
  // Feb 2026 — pull the raw feature-flag status so we can render an
  // admin-only "publicly hidden" banner above the picker when the flag
  // is in `internal_only` mode. The picker itself is already gated by
  // `postedKit.enabled` (server-computed for the current user role).
  const { status: postedKitFlagStatus } = useFeature("posted_kit_paid");

  useEffect(() => {
    let cancelled = false;
    api.get("/public/posted-kit-settings")
      .then(({ data }) => { if (!cancelled) setPostedKit(data || { enabled: false }); })
      .catch(() => { if (!cancelled) setPostedKit({ enabled: false }); });
    return () => { cancelled = true; };
  }, []);

  // Feb 2026 — Bulk Kit picker eligibility. Fetched whenever the
  // resolved partner attribution changes. When neither ?p= nor the
  // stored 90-day blob is present, the endpoint returns
  // `partner_supplied_available=false` with reason='no_attribution'
  // and the picker option stays hidden. Server-side is the source of
  // truth — we never trust `linkedPartner` alone.
  useEffect(() => {
    const slug = partnerSlug || (storedAttribution && storedAttribution.partner_slug) || null;
    let cancelled = false;
    if (!slug) {
      setPartnerKitOptions({ available: false, partner: null, customer_charge_cents: 3400, reason: "no_attribution" });
      return;
    }
    const params = new URLSearchParams();
    if (partnerSlug) params.set("partner_slug", partnerSlug);
    if (!partnerSlug && storedAttribution?.partner_slug) params.set("stored_partner_slug", storedAttribution.partner_slug);
    api.get(`/pre-booking/kit-source-options?${params.toString()}`)
      .then(({ data }) => {
        if (cancelled) return;
        setPartnerKitOptions({
          available:              !!data?.partner_supplied_available,
          partner:                data?.partner || null,
          customer_charge_cents:  Number.isFinite(Number(data?.customer_charge_cents))
            ? Number(data.customer_charge_cents)
            : 3400,
          reason:                 data?.reason || "no_attribution",
        });
      })
      .catch(() => {
        if (!cancelled) setPartnerKitOptions({ available: false, partner: null, customer_charge_cents: 3400, reason: "no_attribution" });
      });
    return () => { cancelled = true; };
  }, [partnerSlug, storedAttribution]);

  // Feb 2026 — All-Inclusive Pricing rollout: fetch local lab
  // collection/drop-off eligibility whenever the customer's property
  // postcode changes. Server owns the feature flag + eligible
  // postcode list. When `reason==="feature_disabled"` the frontend
  // MUST fall back to legacy behaviour (no pricing_model on
  // checkout, no $20 saving badge, no eligibility gate on the two
  // lab options) so the rollout stays behind the master flag.
  const [localLab, setLocalLab] = useState({
    enabled:        false,
    eligible:       false,
    discountCents:  0,
    configuredDiscountCents: 0,
    reason:         "feature_disabled",
    labLocation:    null,
    checkedPostcode: null,
  });
  // Jun 2026 — DIRECT bookings (no partner link, no stored
  // attribution) where the customer already holds a kit handed over by
  // a PWS Partner. They pick the supplying Partner here; the server
  // validates the choice and runs the identical partner-supplied
  // pricing / reimbursement path as a partner-link booking.
  const [supplyingPartners, setSupplyingPartners] = useState({ rows: [], chargeCents: 0 });
  const [selectedSupplyingPartnerId, setSelectedSupplyingPartnerId] = useState("");
  useEffect(() => {
    if (partnerKitOptions.reason !== "no_attribution") return;
    let cancelled = false;
    api.get("/pre-booking/partner-supplied-partner-options")
      .then(({ data }) => {
        if (cancelled) return;
        setSupplyingPartners({
          rows: Array.isArray(data?.partners) ? data.partners : [],
          chargeCents: Number(data?.customer_charge_cents || 0),
        });
      })
      .catch(() => { if (!cancelled) setSupplyingPartners({ rows: [], chargeCents: 0 }); });
    return () => { cancelled = true; };
  }, [partnerKitOptions.reason]);
  const directPartnerKitAvailable =
    !partnerKitOptions.available
    && partnerKitOptions.reason === "no_attribution"
    && supplyingPartners.rows.length > 0;


  useEffect(() => {
    const parsed = parseAusAddress(details.address || "");
    const pc = (parsed.postcode || "").trim();
    let cancelled = false;
    // No postcode yet — still ask the server (without a postcode) so the
    // form knows whether the rollout is on and what saving to advertise.
    // `checkedPostcode` stays null so downstream gates treat the two
    // local-lab options as pending eligibility, not ineligible.
    const url = (!pc || pc.length !== 4)
      ? "/booking/local-lab-eligibility"
      : `/booking/local-lab-eligibility?postcode=${encodeURIComponent(pc)}`;
    api.get(url)
      .then(({ data }) => {
        if (cancelled) return;
        const reason = data?.reason || "feature_disabled";
        setLocalLab({
          // `feature_disabled` is the only reason that means the
          // whole all-inclusive rollout is inactive. All other
          // reasons still mean "feature is on, but this postcode
          // doesn't qualify" — so we treat the rollout as enabled
          // and just suppress the $20 saving.
          enabled:         reason !== "feature_disabled",
          eligible:        !!data?.eligible,
          discountCents:   Number(data?.discount_cents || 0),
          configuredDiscountCents: Number(
            data?.configured_discount_cents || data?.discount_cents || 0
          ),
          reason,
          labLocation:     data?.lab_location || null,
          checkedPostcode: (pc && pc.length === 4) ? pc : null,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setLocalLab((prev) => ({
          ...prev,
          eligible: false,
          discountCents: 0,
          reason: "feature_disabled",
          checkedPostcode: (pc && pc.length === 4) ? pc : null,
        }));
      });
    return () => { cancelled = true; };
  }, [details.address]);

  // Jun 2026 — single source of truth for which kit options are
  // actually offered. Previously the same rules were duplicated in
  // four places (auto-select effect, step validation, picker
  // collapse counter, card render) and the copies had drifted: under
  // all-inclusive pricing `postedKit.enabled` is false, so mail-out
  // was counted as hidden and the picker collapsed even when two
  // real options (mail-out + partner-supplied) were on screen.
  //
  // Rules (unchanged in substance):
  //  • The POSTCODE gate applies ONLY to the two public lab options.
  //  • partner_supplied is postcode-independent — attribution, stock
  //    and the partner's allow flag are the only gates.
  //  • mail_out is always offered under all-inclusive pricing unless
  //    an attributed partner explicitly turned it off.
  const localLabGate = !localLab.enabled
    || localLab.eligible
    || !localLab.checkedPostcode;
  const visibleKitOptions = useMemo(() => {
    const out = [];
    if ((!partnerKitOptions.partner || !!partnerKitOptions.partner.allow_lab_pickup)
        && localLabGate) out.push("local_pickup_dropoff");
    if ((!partnerKitOptions.partner || !!partnerKitOptions.partner.allow_customer_dropoff)
        && localLabGate) out.push("existing_bottle");
    if ((localLab.enabled || !!postedKit.enabled)
        && (!partnerKitOptions.partner
            || partnerKitOptions.partner.allow_mailout_kit !== false)) out.push("mail_out");
    if (partnerKitOptions.available || directPartnerKitAvailable) out.push("partner_supplied");
    return out;
  }, [
    partnerKitOptions.available, partnerKitOptions.partner,
    directPartnerKitAvailable,
    postedKit.enabled, localLab.enabled, localLabGate,
  ]);

  // Auto-select the kit method ONLY when a single option is visible
  // (mirrors the picker's `<= 1` short-circuit that hides the picker
  // entirely). With 0 or 2+ options we leave `kitMethod` empty so the
  // customer must pick — that gates "Continue" on Step 2 and stops a
  // fee being added to the Order Summary by default.
  useEffect(() => {
    if (visibleKitOptions.length === 1) {
      if (kitMethod !== visibleKitOptions[0]) setKitMethod(visibleKitOptions[0]);
      return;
    }
    if (kitMethod && !visibleKitOptions.includes(kitMethod)) setKitMethod("");
  }, [visibleKitOptions, kitMethod]);


  // Feb-2026 (Option B) — Deep-link support for the upcoming homepage
  // Hero "Order a Test Kit" CTA. If the URL contains `?kit=mail_out`
  // AND the paid posted-kit flag is visible for this caller, pre-select
  // the mail-out option so the customer lands directly on the postal
  // kit flow. If the flag is off, we quietly do nothing (the picker
  // isn't rendered anyway, and the backend defaults kit_method to
  // "mail_out" server-side for compatibility). Only reads on mount.
  useEffect(() => {
    if (!postedKit?.enabled) return;
    const params = new URLSearchParams(window.location.search);
    const kitParam = (params.get("kit") || "").toLowerCase();
    if (kitParam === "mail_out" || kitParam === "mail-out" || kitParam === "postal") {
      setKitMethod("mail_out");
    } else if (kitParam === "local_pickup_dropoff" || kitParam === "pickup" || kitParam === "dropoff") {
      setKitMethod("local_pickup_dropoff");
    }
  }, [postedKit?.enabled]);

  // Phase 2L-A116 — Property Manager invoice payment option. The
  // booking page probes `/api/property-manager/me` once when a user is
  // logged in. Only approved PM partners may select `Invoice (account)`
  // and supply a PO number; everyone else sees the standard Stripe
  // path with no extra UI.
  const [pmPartner, setPmPartner] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // 'stripe' | 'invoice'
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState("");
  // Phase 2L-A121.B — managed-property lock + tenant/site contact form.
  // `lockedProperty` is the canonical record from /api/passport/property/{id}
  // used to render the read-only property card so the PM can't accidentally
  // edit address/suburb/postcode and create a duplicate.
  const [lockedProperty, setLockedProperty] = useState(null);
  const [siteContact, setSiteContact] = useState({
    name: "", phone: "", email: "", access_notes: "",
  });
  useEffect(() => {
    if (!user?.is_property_manager) { setPmPartner(null); return; }
    api.get("/property-manager/me")
      .then(({ data }) => setPmPartner(data?.partner || null))
      .catch(() => setPmPartner(null));
  }, [user]);
  const isPropertyManager = !!pmPartner;
  // PM context is active when the PM arrived via `?property=...` AND that
  // property belongs to their portfolio. Used to gate the locked property
  // card + tenant/site contact form + booking authority banner.
  const pmContextActive = isPropertyManager && !!lockedProperty;

  // Fetch membership status if user is logged in — drives member pricing display
  useEffect(() => {
    if (!user) { setMembership(null); return; }
    api.get("/memberships/me").then(({ data }) => setMembership(data)).catch(() => setMembership(null));
  }, [user]);

  // Phase 2L-A80 — voucher lookup. Single fire on mount when `?invite=`
  // is present. We deliberately don't poll: the customer's checkout
  // submission re-validates server-side, and the voucher state shown
  // here is purely advisory ("$50 voucher applied").
  useEffect(() => {
    if (!initialInviteToken) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/invite/voucher/${encodeURIComponent(initialInviteToken)}`);
        if (!alive) return;
        if (r.data?.valid) {
          setVoucher(r.data);
          setVoucherErr("");
        } else {
          setVoucher(null);
          const reasonMap = {
            expired: "This invite voucher has expired.",
            redeemed: "This invite voucher has already been used.",
            applied: "This invite voucher is currently being used in another checkout.",
            voided: "This invite voucher is no longer active.",
            feature_disabled: "Invite vouchers are temporarily paused.",
            unknown: "This invite link doesn't match any voucher.",
          };
          setVoucherErr(reasonMap[r.data?.reason] || "Invite voucher unavailable.");
        }
      } catch {
        if (alive) setVoucherErr("Couldn't verify the invite voucher right now.");
      }
    })();
    return () => { alive = false; };
  }, [initialInviteToken]);

  const isMember = !!membership?.is_member;

  // Aware Feb-2026 — service-area conversion guard removed. Previously,
  // if an entered address matched an "excluded" suburb (Gympie /
  // Glasshouse) the form surfaced a rose-tinted "we don't service
  // this area" banner and CTA. Aware is now a national posted-kit
  // service, so we no longer block or discourage any AU address.
  // Region allocation (internal partner-matching) still runs on the
  // backend, but it never gates the booking.

  // Phase 30 — fetch optional sample collection availability for the
  // attributed partner whenever the partner OR the customer postcode
  // changes. Display-only; never blocks the booking, never charges,
  // never schedules.
  useEffect(() => {
    if (!linkedPartner?.partner_slug) {
      setSampleCollection(null);
      return;
    }
    const parsed = parseAddress(details.address || "");
    const postcode = parsed.postcode || "";
    let cancelled = false;
    api.get("/public/pws-sample-collection/availability", {
      params: { partner_slug: linkedPartner.partner_slug, postcode },
    })
      .then(({ data }) => { if (!cancelled) setSampleCollection(data); })
      .catch(() => { if (!cancelled) setSampleCollection(null); });
    return () => { cancelled = true; };
  }, [linkedPartner, details.address]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  // One-Click Rebooking: pre-fill customer details from the chosen property's last booking
  useEffect(() => {
    if (!prefillPropertyId) return;
    const token = localStorage.getItem("scwt_token");
    if (!token) return;
    api.get(`/passport/property/${prefillPropertyId}`).then(({ data }) => {
      const p = data.property;
      if (!p) return;
      const sourceType = p.sources?.[0]?.type;
      // Map source types onto sample types. `town` / `filtered` previously
      // mapped to `town-water` / `filtered`, but Aware no longer offers town
      // water testing (Feb-2026). Fall back to tank-rainwater so the
      // booking flow remains usable.
      const sampleType = ({
        rainwater_tank: "tank-rainwater", bore: "bore-water",
      })[sourceType] || "tank-rainwater";
      setDetails((d) => ({
        ...d,
        email: d.email || p.owner_email || "",
        address: d.address || p.address || "",
        sampleType,
      }));
      setRebookInfo({ address: p.address, nickname: p.nickname });
      // Phase 2L-A121.B — snapshot the property record so the booking
      // form can render a locked card (PM context) instead of an
      // editable address field. Only sets `lockedProperty` when the
      // PM is the manager-of-record (managed_by_partner_id matches the
      // logged-in PM partner), so a homeowner rebooking their own
      // property still sees the regular editable flow.
      if (pmPartner?.id && p.managed_by_partner_id === pmPartner.id) {
        setLockedProperty(p);
      }
      toast.success("Pre-filled from your AWARE Water Health Record");
    }).catch(() => {});
  }, [prefillPropertyId, pmPartner]);

  const test = livePricedTests.find((t) => t.id === selectedTest);
  // Universal online prepaid pricing (20% off retail) — applied for everyone.
  // Members get the LOWER of `member` and `online_prepaid` so they never pay more.
  const onlinePrice = test ? test.online_prepaid : 0;
  // Phase 9 — server-authoritative pricing quote. Re-fetched whenever the
  // selected test OR the linked partner slug changes so the customer always
  // sees the same price Stripe will charge.
  const [priceQuote, setPriceQuote] = useState(null);
  // Phase 9 — bulk quote map (one per test_id) so the choose-test cards
  // can show partner prices when a partner slug is present. Only
  // refreshed when partnerSlug changes — local-only pricing is already
  // baked into the static TESTS catalog.
  const [partnerQuoteByTest, setPartnerQuoteByTest] = useState({});
  useEffect(() => {
    let cancelled = false;
    if (!partnerSlug) { setPartnerQuoteByTest({}); return; }
    Promise.all(["basic", "tank", "bore"].map(tid =>
      api.get(`/pricing/quote?test_id=${tid}&partner_slug=${encodeURIComponent(partnerSlug)}`)
        .then(r => [tid, r.data])
        .catch(() => [tid, null])
    )).then(entries => {
      if (cancelled) return;
      const map = {};
      for (const [tid, data] of entries) {
        if (data && data.pricing_mode === "national_partner") map[tid] = data;
      }
      setPartnerQuoteByTest(map);
    });
    return () => { cancelled = true; };
  }, [partnerSlug]);
  useEffect(() => {
    let cancelled = false;
    if (!selectedTest) { setPriceQuote(null); return; }
    const params = new URLSearchParams({ test_id: selectedTest });
    // partnerSlug ⇒ priority 1 (current-session ?p= attribution).
    if (partnerSlug) params.set("partner_slug", partnerSlug);
    api.get(`/pricing/quote?${params.toString()}`)
      .then(({ data }) => { if (!cancelled) setPriceQuote(data); })
      .catch(() => { if (!cancelled) setPriceQuote(null); });
    return () => { cancelled = true; };
  }, [selectedTest, partnerSlug]);
  const partnerNationalActive =
    !!(priceQuote && priceQuote.pricing_mode === "national_partner") ||
    !!(selectedTest && partnerQuoteByTest[selectedTest]);
  // Phase 2L-A132C — PMs charge at retail / standard invoice pricing
  // (no 20%-off online prepaid discount). All other users (anonymous,
  // member, regular customer) keep the existing online_prepaid path so
  // the public booking flow remains identical.
  // Feb 2026 — regular customers additionally collapse to retail when
  // the central `online_discount.enabled` switch is off (admin toggle).
  const localUnitPrice = test
    ? (isPropertyManager
        ? test.retail
        : (isMember
            ? Math.min(test.member, test.online_prepaid)
            : (discountEnabled ? test.online_prepaid : test.retail)))
    : 0;
  const activePartnerQuote = priceQuote && priceQuote.pricing_mode === "national_partner"
    ? priceQuote
    : (selectedTest ? partnerQuoteByTest[selectedTest] : null) || null;
  const unitPrice = activePartnerQuote
    ? Number(activePartnerQuote.final_price)
    : localUnitPrice;
  const subtotal = unitPrice;
  // Phase 2L-A3: total reflects any applied discount; subtotal is the
  // pre-discount price displayed alongside the discount line.
  // Phase 2J-AJ: when the Passport add-on is ticked, add the locked
  // server price to the displayed total so the customer sees what they
  // will actually be charged.
  const testTotal = discount ? Number(discount.final_total) : subtotal;
  const addonAmount = passportAddon ? PASSPORT_ADDON_CENTS / 100 : 0;
  // Feb-2026 bugfix — the sidebar Total must match what Stripe will
  // charge, or the customer sees a mismatch between the booking page
  // and the hosted checkout. Include the posted-kit postage fee
  // whenever (a) the flag surfaces the option, (b) the customer picked
  // "post a kit to me". Any other combination = fee is $0.
  const postedKitFeeAmount = (postedKit.enabled && kitMethod === "mail_out")
    ? ((postedKit.fee_cents || 0) / 100)
    : 0;
  // Feb 2026 — Bulk Kit customer picker. When the customer selected
  // "Kit supplied by my PWS Partner", the $34 partner-kit charge is
  // added as a separate Stripe line item. Mutually exclusive with the
  // posted-kit fee (server refuses the combo).
  const partnerKitSelected = kitMethod === "partner_supplied"
    && (partnerKitOptions.available || directPartnerKitAvailable);
  const partnerKitChargeAmount = partnerKitSelected
    ? ((partnerKitOptions.available
        ? partnerKitOptions.customer_charge_cents
        : supplyingPartners.chargeCents) / 100)
    : 0;
  // Feb 2026 — All-Inclusive Pricing rollout. The $20 local-lab
  // saving fires when (a) the master flag is on, (b) the customer
  // has been confirmed eligible for their postcode, AND (c) they
  // picked one of the two lab-attendance kitMethod values. Server
  // re-validates on /payments/checkout — this is UX-only.
  const localLabSelected =
    kitMethod === "local_pickup_dropoff" || kitMethod === "existing_bottle";
  // Jun 2026 — the $20 local-lab saving never stacks with a discount
  // code (server enforces the same rule on /payments/checkout).
  const localLabSavingAmount =
    localLab.enabled && localLab.eligible && localLabSelected && !discount
      ? (localLab.discountCents / 100)
      : 0;
  // Jun 2026 — LWH member discount (never stacks with a discount code;
  // server enforces the same rule on /payments/checkout).
  const lwhDiscountAmount = (!discount && lwhDiscount?.applied)
    ? Number(lwhDiscount.amount_dollars || 0)
    : 0;
  const total = testTotal + addonAmount + postedKitFeeAmount + partnerKitChargeAmount - localLabSavingAmount - lwhDiscountAmount;

  // Feb-2026 bugfix — resolve the effective postal address the customer
  // wants the kit mailed to. When the source toggle is "property" we
  // reuse the address the customer already typed into the main address
  // field on Step 2 (parsed into structured components); when "custom"
  // we use whatever they typed into the postal form directly. The
  // resulting dict is what goes to /payments/checkout as
  // `posted_kit_address`.
  // Nov 2026 (SCWT-34E6D6) — `postedKitAddress` is now the SINGLE source
  // of truth for the structured delivery address regardless of source
  // toggle. When "property" is selected, keep it synced to a fresh parse
  // of the property address (parseAusAddress now correctly splits
  // comma-less addresses too) — but this only fires on a source/address
  // CHANGE, never on every keystroke inside the postal fields, so a
  // customer's manual correction of a bad split is never clobbered.
  useEffect(() => {
    if (postalAddressSource !== "property") return;
    const parsed = parseAusAddress(details.address || "");
    setPostedKitAddress({
      line1:    parsed.line1    || "",
      line2:    parsed.line2    || "",
      suburb:   parsed.suburb   || "",
      state:    parsed.state    || "",
      postcode: parsed.postcode || "",
      country:  parsed.country  || "Australia",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalAddressSource, details.address]);
  const onlineSavings = test ? (test.retail - test.online_prepaid) : 0;
  const memberSavings = test ? Math.max(0, test.online_prepaid - test.member) : 0;

  // Feb 2026 — Passport add-on retired. This effect + setter are no-ops
  // now, kept only so surrounding useEffect deps don't churn.
  useEffect(() => {
    // no-op — passportAddon is permanently false
  }, [isMember]);

  // Re-validate a code whenever the test or member price changes — keeps
  // the cart honest if the customer switches tests after applying.
  useEffect(() => {
    if (!discount) return;
    let cancelled = false;
    api.post("/discounts/validate", {
      code: discount.code, subtotal,
      customer_email: details.email || undefined,
      test_id: selectedTest,
    }).then(({ data }) => {
      if (cancelled) return;
      if (data.valid) setDiscount(data);
      else { setDiscount(null); setDiscountErr(data.message || "This discount code is not valid."); }
    }).catch(() => { /* leave existing discount as-is; user can re-apply */ });
    return () => { cancelled = true; };
  }, [selectedTest, subtotal]);

  // Jun 2026 — LWH member-discount preview. Fires only when the booking
  // arrived via /book?property=… (retest reminder / passport link) and no
  // discount code is applied. Base mirrors the server: all-inclusive mode
  // discounts the package after the local-lab saving; legacy mode uses
  // the test price (postage/partner-kit are separate line items).
  useEffect(() => {
    if (!prefillPropertyId || !selectedTest || !subtotal || discount) {
      setLwhDiscount(null);
      return;
    }
    let cancelled = false;
    const base = localLab.enabled
      ? Math.max(0, subtotal - localLabSavingAmount)
      : subtotal;
    api.post("/public/water-health/discount-preview", {
      property_id: prefillPropertyId,
      test_id: selectedTest,
      quoted_final_price: base,
    }).then(({ data }) => {
      if (!cancelled) setLwhDiscount(data?.applied ? data : null);
    }).catch(() => { if (!cancelled) setLwhDiscount(null); });
    return () => { cancelled = true; };
  }, [prefillPropertyId, selectedTest, subtotal, discount, localLab.enabled, localLabSavingAmount]);

  const applyDiscount = async () => {
    const code = discountInput.trim();
    if (!code) { setDiscountErr("Please enter a discount code."); return; }
    setApplyingDiscount(true);
    setDiscountErr("");
    try {
      const { data } = await api.post("/discounts/validate", {
        code, subtotal,
        customer_email: details.email || undefined,
        test_id: selectedTest,
      });
      if (data.valid) {
        setDiscount(data);
        toast.success(data.message || "Discount code applied.");
      } else {
        setDiscount(null);
        setDiscountErr(data.message || "This discount code is not valid.");
      }
    } catch (err) {
      setDiscount(null);
      const msg = err?.response?.data?.detail;
      setDiscountErr(typeof msg === "string" ? msg : "This discount code is not valid.");
    } finally {
      setApplyingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setDiscount(null);
    setDiscountInput("");
    setDiscountErr("");
  };

  // Jun 2026 — letter/offer deep links (?discount=CODE) prefill the code
  // and auto-apply once pricing is ready. The price itself is always
  // computed and validated server-side — the URL only carries the code.
  const urlDiscount = searchParams.get("discount") || "";
  const [urlDiscountApplied, setUrlDiscountApplied] = useState(false);
  useEffect(() => {
    if (!urlDiscount || urlDiscountApplied || !subtotal) return;
    setUrlDiscountApplied(true);
    setDiscountInput(urlDiscount);
    (async () => {
      try {
        const { data } = await api.post("/discounts/validate", {
          code: urlDiscount, subtotal, test_id: selectedTest,
        });
        if (data.valid) {
          setDiscount(data);
          toast.success(data.message || "Your offer code has been applied.");
        } else {
          setDiscountErr(data.message || "This discount code is not valid.");
        }
      } catch { /* silent — customer can still apply manually */ }
    })();
  }, [urlDiscount, urlDiscountApplied, subtotal, selectedTest]);

  // Jun 2026 — In-Field Booking prefill: customer details from the
  // inspection appointment + fulfillment locked to "pick up sample bottle"
  // (the technician already collected the sample on site).
  useEffect(() => {
    if (!fieldBooking) return;
    const c = fieldBooking.customer || {};
    setDetails((d) => ({ ...d, name: c.name || "", mobile: c.mobile || "", email: c.email || "", address: c.address || "" }));
    setDiscountInput(fieldBooking.code);
    setKitMethod("local_pickup_dropoff");
  }, []); // eslint-disable-line

  // Auto-apply the instant 20% inspection discount once the cart has a price.
  const fieldDiscountTried = useRef(false);
  useEffect(() => {
    if (!fieldBooking || discount || fieldDiscountTried.current || !subtotal || !selectedTest) return;
    fieldDiscountTried.current = true;
    api.post("/discounts/validate", {
      code: fieldBooking.code, subtotal,
      customer_email: fieldBooking.customer?.email || undefined,
      test_id: selectedTest,
    }).then(({ data }) => {
      if (data.valid) {
        setDiscount(data);
        toast.success("20% inspection discount applied.");
      } else {
        setDiscountErr(data.message || "The inspection discount could not be applied.");
      }
    }).catch(() => {});
  }, [fieldBooking, discount, subtotal, selectedTest]);

  const validateStep2 = () => {
    // Phase 2L-A121.B — when the PM arrives via ?property=<id> and the
    // property is in their portfolio, we collect Tenant / Site Contact
    // details instead of homeowner Your Details. The address is taken
    // from the locked property record, not from a user input.
    if (pmContextActive) {
      if (!siteContact.name.trim()) return "Please enter the tenant / site contact name";
      if (!/^[\d\s+()-]{8,}$/.test(siteContact.phone.trim())) return "Please enter a valid tenant / site contact phone";
      if (siteContact.email.trim() && !/^\S+@\S+\.\S+$/.test(siteContact.email.trim())) {
        return "Please enter a valid tenant / site contact email (or leave blank)";
      }
      return null;
    }
    if (!details.name.trim()) return "Please enter your name";
    if (!/^[\d\s+()-]{8,}$/.test(details.mobile.trim())) return "Please enter a valid mobile number";
    if (!/^\S+@\S+\.\S+$/.test(details.email.trim())) return "Please enter a valid email";
    if (!details.address.trim()) return "Please enter your property address";
    // Phase 2L-A39 — Reason-for-Testing and Primary-Water-Use validation
    // dropped along with the dropdowns themselves. Fields remain on the
    // payload (server-side `Optional`) so legacy admin/lab records keep
    // their existing shape.
    if (intake) {
      const intakeErr = validateIntakeForm(intake);
      if (intakeErr) return intakeErr;
    } else {
      return "Please complete the Property & Water System Information section";
    }
    // Feb 2026 — require an explicit kit-source pick when 2+ options
    // are visible. Mirrors the picker's single-option short-circuit
    // so single-option flows (which auto-select) still pass through.
    if (visibleKitOptions.length >= 2 && !kitMethod) {
      return "Please choose how you'd like to receive your sample kit";
    }
    // Jun 2026 — a direct (non-partner-link) booking must name the
    // Partner who supplied the kit before it can proceed.
    if (kitMethod === "partner_supplied"
        && directPartnerKitAvailable
        && !selectedSupplyingPartnerId) {
      return "Please choose the PWS Partner who supplied your sample kit";
    }
    // Nov 2026 (SCWT-34E6D6) — a mail-out booking must have a reviewed,
    // valid structured delivery address before the customer can proceed
    // to payment. This is the single checkout-time gate that stops a
    // bad/empty address parse from silently reaching Stripe (the root
    // cause of 30 paid mail-out bookings that could never get a
    // shipping label).
    if (kitMethod === "mail_out") {
      const a = postedKitAddress;
      if (!(a.line1 || "").trim())   return "Please enter the street address for your posted kit";
      if (!(a.suburb || "").trim())  return "Please enter the suburb for your posted kit";
      if (!(a.state || "").trim())   return "Please enter the state for your posted kit";
      if (!/^\d{4}$/.test((a.postcode || "").trim()))
        return "Please enter a valid 4-digit postcode for your posted kit";
    }
    return null;
  };

  // Auto-derive sampleType from the booked test so the lab/admin still
  // know what kind of water came in without asking the customer twice.
  // Town water removed Feb-2026 — Basic test repositioned as a general
  // safety screen for private water supplies (tank / bore default).
  const TEST_DEFAULT_SAMPLE_TYPE = {
    basic: "tank-rainwater",      // AWARE 4-Point → defaults to tank
    tank:  "tank-rainwater",      // AWARE Tank 360
    bore:  "bore-water",          // AWARE Bore 360
  };
  const resolvedSampleType = details.sampleType || TEST_DEFAULT_SAMPLE_TYPE[selectedTest] || "tank-rainwater";
  // Phase 2L-A121.B — when the PM is booking on a managed property, we
  // construct the `customer` payload from the agency identity (so labs
  // still have a real contact on file) plus the locked property address.
  // Tenant / on-site contact lives separately in `site_contact` on the
  // PM booking endpoint — never substituted into the customer block.
  const customerPayload = pmContextActive ? {
    name:    pmPartner?.contact_name || pmPartner?.business_name || user?.full_name || "",
    mobile:  pmPartner?.phone || "",
    email:   pmPartner?.email || user?.email || "",
    address: lockedProperty?.address || "",
    sampleType: resolvedSampleType,
    reason_for_testing: "",
    reason_for_testing_other: "",
    primary_water_use: "",
    primary_water_use_other: "",
  } : { ...details, sampleType: resolvedSampleType };

  const next = () => {
    if (step === 2) {
      const err = validateStep2();
      if (err) { toast.error(err); return; }
      // Capture cart at the moment user reaches payment — enables abandoned-cart recovery
      api.post("/cart/capture", {
        test_id: selectedTest,
        expert_advice: expertAdvice,
        customer: customerPayload,
      }).catch(() => {});  // fire-and-forget; never block the flow
    }
    setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(1, s - 1));

  const proceedToStripe = async () => {
    setSubmitting(true);
    // Phase 27 — checkout_started event (beacon so it survives unload).
    trackEvent("booking_checkout_started", {
      partnerSlug: partnerSlug || null,
      testId: (selectedTest && selectedTest.id) || null,
      attributionSource: partnerSlug
        ? "partner_link"
        : (storedAttribution ? "stored_partner_attribution" : "direct_scwt"),
    }, { useBeacon: true });
    try {
      const { data } = await api.post("/payments/checkout", {
        test_id: selectedTest,
        expert_advice: expertAdvice,
        customer: customerPayload,
        origin_url: window.location.origin,
        property_info: intake ? intakeFormToPayload(intake) : null,
        discount_code: discount?.code || null,
        // Phase 2J-AJ — Passport add-on; server re-validates the price.
        passport_addon: passportAddon,
        customer_sample_reference: customerSampleReference.trim() || null,
        sample_point: samplePoint.trim() || null,
        // Jun 2026 — LWH member discount. Forward the property id from
        // /book?property=… so the server can apply the member discount.
        property_id: prefillPropertyId || null,
        // Phase 8 — Mail-Out Sample Kit. Customer picked the kit method
        // on the Sample-Kit step; server re-validates against the
        // whitelist and falls back to "mail_out" if invalid. When the
        // partner-supplied option is chosen we forward "local_pickup_dropoff"
        // because the customer physically collects the kit from the
        // partner and the AWARE-post fulfilment path is not used.
        kit_method: partnerKitSelected ? "local_pickup_dropoff" : (kitMethod || "mail_out"),
        // Feb 2026 — Bulk Kit customer picker + All-Inclusive Pricing
        // rollout. Map the UI-side kitMethod onto the canonical
        // `kit_source` enum (see `backend/core/kit_source_enum.py`).
        // When the All-Inclusive rollout is OFF, keep the legacy
        // shape ("aware_kit" / "partner_supplied") so bookings created
        // during a rollback are stamped in the exact same way as
        // pre-rollout ones. Server re-validates every value.
        kit_source: (() => {
          if (partnerKitSelected) return "partner_supplied";
          if (!localLab.enabled) return "aware_kit"; // legacy pre-rollout
          if (kitMethod === "local_pickup_dropoff") return "lab_collection_return";
          if (kitMethod === "existing_bottle")      return "customer_lab_dropoff";
          if (kitMethod === "mail_out")             return "aware_mailout";
          return "aware_mailout"; // safe default under the rollout
        })(),
        // Feb 2026 — service postcode used ONLY for local-lab
        // eligibility gating on the server. Parsed from the property
        // address the customer already entered. When absent the
        // server treats the booking as ineligible for the $20 saving.
        service_postcode: (() => {
          const parsed = parseAusAddress(details.address || "");
          return parsed.postcode || null;
        })(),
        // Feb 2026 — All-Inclusive Pricing model opt-in. Only sent
        // when the master flag returned `enabled` from the eligibility
        // endpoint. Server re-validates against the same flag; the
        // field is IGNORED (legacy pricing path runs) when the
        // rollout is off, keeping in-flight sessions safe during
        // rollout / rollback.
        pricing_model: localLab.enabled ? "all_inclusive_v1" : null,
        // Jun 2026 — the Partner the customer says supplied their kit
        // on a DIRECT booking (no partner link). Server re-validates
        // it is ACTIVE, kit-supplied-enabled and in stock, then uses
        // it for attribution + the existing reimbursement path.
        supplying_partner_id:
          partnerKitSelected && directPartnerKitAvailable
            ? (selectedSupplyingPartnerId || null)
            : null,
        // Phase 2L-A144 — Paid posted-kit fields. Only forwarded when
        // the flag is enabled AND the customer picked the "post a kit
        // to me" option (mail_out). Server re-validates the address
        // and adds the fee as a separate Stripe line item; a malformed
        // or missing address returns 400 rather than silently dropping
        // the fee.
        // Jun 2026 fix — the shipping address must be forwarded for EVERY
        // mail-out booking, not only when the separate posted-kit fee flag
        // is on. Under all-inclusive pricing (postage baked into the
        // sticker price) `postedKit.enabled` is false, which used to strip
        // the address from checkout — bookings then had no
        // posted_kit_address stamped, vanished from the labels-to-create
        // count and could never generate a Starshipit label.
        posted_kit_address:
          kitMethod === "mail_out"
            ? {
                line1:    (postedKitAddress.line1 || "").trim(),
                line2:    (postedKitAddress.line2 || "").trim() || null,
                suburb:   (postedKitAddress.suburb || "").trim(),
                state:    (postedKitAddress.state || "").trim(),
                postcode: (postedKitAddress.postcode || "").trim(),
                country:  (postedKitAddress.country || "Australia").trim(),
              }
            : null,
        posted_kit_delivery_notes:
          kitMethod === "mail_out"
            ? (postedKitDeliveryNotes || "").trim() || null
            : null,
        // Phase 2L-A80 — Invite-a-Neighbour voucher. Only forwards the
        // token when our advisory lookup said the voucher was valid;
        // the server re-validates and atomically claims regardless.
        invite_token: voucher?.valid ? inviteToken : null,
        // Phase 2 (Aware Partner Network) — partner-link attribution.
        // Forward the slug whenever it's present on the URL; the server
        // re-resolves and only stamps attribution for ACTIVE partners.
        partner_slug: partnerSlug || null,
        // Phase 3 — 90-day persistence. When the customer is returning
        // through the bare URL but has a non-expired blob in localStorage,
        // we forward it; the server re-validates expiry + ACTIVE partner.
        // The blob is ignored server-side when partner_slug is also set
        // (current-session click wins).
        stored_partner_attribution:
          !partnerSlug && storedAttribution ? storedAttribution : null,
        // Jun 2026 — Partner Platform Phase 3: referral-link commission
        // attribution (separate from PWS allocation above). Slug saved
        // by /go/{slug}; expires client-side after 90 days; the server
        // re-resolves against can_refer partners and never allocates
        // the booking to the referral partner.
        referral_slug: (() => {
          try {
            const blob = JSON.parse(localStorage.getItem("aware_referral") || "null");
            if (blob?.slug && Date.now() - (blob.at || 0) < 90 * 24 * 3600 * 1000) return blob.slug;
          } catch { /* ignore */ }
          return null;
        })(),
        // Phase 25 — Customer Consent Wording. Optional explicit consent
        // captured on Step 3 of the booking flow. The server persists the
        // canonical disclosure text + version + ISO timestamp + source on
        // the new booking. None = `not_captured`; never backfills.
        partner_support_consent: partnerSupportConsent,
        // Jun 2026 — In-Field Technician Booking. Server re-verifies the
        // inspection and only stamps technician_collected when the sample
        // was genuinely recorded as collected.
        field_inspection_id: fieldBooking?.inspection_id || null,
      });
      if (data.url) {
        if (fieldBooking) {
          // Field mode — the CUSTOMER pays on their own device via QR /
          // payment link. Return to the inspection screen which shows the
          // QR + waiting-for-payment panel (server stamps payment_url).
          localStorage.setItem("aware_field_booking", JSON.stringify({
            ...fieldBooking, session_id: data.session_id, reference: data.reference,
          }));
          navigate(`/field/inspection/${fieldBooking.appointment_id}?pay=1`);
          return;
        }
        window.location.href = data.url;
      } else {
        toast.error("Unable to start checkout. Please try again.");
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout failed. Please try again.");
      setSubmitting(false);
    }
  };

  // Phase 2L-A116 — Property Manager INVOICE booking submission.
  // Skips Stripe entirely. Server FORCES `payment_mode=invoice`,
  // `payment_method=invoice_account`, sends the invoice email to the
  // PM agency email immediately, and writes the audit log row.
  //
  // Phase 2L-A130 hotfix — also run the same `validate()` gate the
  // Stripe path uses BEFORE firing the request. Pre-A130 the invoice
  // path skipped validation, so a PM who scrolled past Step 2 with
  // `filtration` still empty (the questionnaire form mounts with
  // `filtration: ""`, which fails the server-side Pydantic regex
  // `^(whole_house|under_sink|both|none|unsure)$`) would see a 422
  // come back as a generic toast and reasonably blame whichever
  // field they last touched — usually the PO. The validate() call
  // surfaces the real cause ("Please tell us about filtration") at
  // the right layer, so the PM can complete the questionnaire and
  // re-submit cleanly.
  const proceedToInvoice = async () => {
    if (!isPropertyManager) return;
    const po = (purchaseOrderNumber || "").trim();
    if (!po) {
      toast.error("Purchase Order number is required for invoice bookings.");
      return;
    }
    const valErr = validateStep2();
    if (valErr) {
      toast.error(valErr);
      // Drop the user back to Step 2 so they can fix the missing
      // questionnaire answer in context rather than guessing.
      setStep(2);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/property-manager/bookings", {
        test_id: selectedTest,
        expert_advice: expertAdvice,
        customer: customerPayload,
        purchase_order_number: po,
        property_id: (lockedProperty?.id) || prefillPropertyId || null,
        // Phase 2L-A121.B — only forward the property_info intake when the
        // PM filled it in. Backend will back-fill from the locked property
        // record when omitted.
        // Phase 2L-A130 hotfix — also drop the payload when `filtration`
        // is still the empty-string default. The questionnaire form mounts
        // with `filtration: ""` and immediately syncs upward via its
        // `onChange(EMPTY)` useEffect, so `intake` is non-null long
        // before the PM actually answers the question. Sending the
        // empty string would fail the server-side regex
        // `^(whole_house|under_sink|both|none|unsure)$` with a 422.
        // The server back-fills from `linked_property` when omitted.
        property_info: (intake && intake.filtration)
          ? intakeFormToPayload(intake)
          : null,
        customer_sample_reference: customerSampleReference.trim() || null,
        sample_point: samplePoint.trim() || null,
        // Phase 2L-A121.B — tenant / on-site contact captured in Step 2.
        site_contact: pmContextActive && (siteContact.name.trim() || siteContact.phone.trim()) ? {
          name: siteContact.name.trim(),
          phone: siteContact.phone.trim(),
          email: siteContact.email.trim() || null,
          access_notes: siteContact.access_notes.trim() || null,
        } : null,
      });
      const ref = data?.booking?.reference;
      const sent = data?.invoice?.customer_email_status === "sent";
      pushFormEvent("booking_form_submit", {
        form_name: "water_test_booking",
        form_type: "booking",
        booking_type: "water_test_invoice",
        test_type: selectedTest || null,
        booking_reference: ref || null,
      });
      if (sent) {
        toast.success("Invoice emailed — payment pending.");
      } else {
        toast.success("Invoice booking created.");
      }
      navigate(`/track/${encodeURIComponent(ref || "")}?invoice=1`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create invoice booking. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={BOOKING.page} className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <BrandLogo to="/" size="sm" />
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500">
            <ShieldCheck className="w-3.5 h-3.5" />
            Secure Stripe checkout
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <BackToPortfolioButton className="mb-6" />

        {/* Feb 2026 — Australia-wide shipping hero banner. Placed ABOVE
            the page title so it is the first thing customers see. Dark
            navy panel + emerald/cyan accents + real mail-kit photo for
            maximum visibility. */}
        {/* Feb 2026 — Australia-wide shipping hero. Single pre-designed
            artwork with rounded corners. */}
        <div
          data-testid="booking-shipping-banner"
          className="mb-8 rounded-2xl overflow-hidden ring-1 ring-sky-200 shadow-sm"
        >
          <img
            src="/booking-hero-aust-wide-v3.webp"
            alt="AWARE Water Testing — sample kits shipped to every corner of Australia. Postage included, dispatched via AusPost with a prepaid Express Post return label from the Sunshine Coast Queensland laboratory."
            className="block w-full h-auto"
            loading="eager"
            data-testid="booking-shipping-banner-image"
          />
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
          Book a Water Test
        </h1>
        <p className="text-base text-slate-600 mb-8 max-w-2xl">
          Choose your test, enter your details, and complete secure online checkout — your sample kit instructions arrive immediately.
        </p>
        {rebookInfo && (
          <div data-testid="booking-rebook-banner" className="mb-6 rounded-2xl bg-cyan-50 border border-cyan-200 p-4 flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-cyan-600 flex-shrink-0" />
            <div className="text-sm">
              <span className="font-semibold text-slate-900">One-click rebook</span>
              <span className="text-slate-600"> — Pre-filled from your AWARE Water Health Record for {rebookInfo.nickname || rebookInfo.address}.</span>
            </div>
          </div>
        )}
        {fieldBooking && (
          <div data-testid="field-booking-banner" className="mb-6 rounded-2xl bg-emerald-50 border border-emerald-300 p-4 flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <div className="text-sm">
              <span className="font-semibold text-slate-900">In-Field Booking</span>
              <span className="text-slate-600"> — booking on behalf of {fieldBooking.customer?.name || "the homeowner"}. Details prefilled from the Water Health Inspection; the 20% discount applies automatically and the sample bottle has already been collected.</span>
            </div>
          </div>
        )}
        {linkedPartner && (
          <div
            data-testid="partner-attribution-banner"
            data-attribution-source={attributionFromStorage ? "stored" : "partner_link"}
            className="mb-6 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-center gap-3"
          >
            <ShieldCheck className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="text-sm">
              {attributionFromStorage ? (
                <>
                  <span className="font-semibold text-slate-900">
                    Welcome back — booking via {linkedPartner.business_name}
                  </span>
                  {(linkedPartner.franchise_region || linkedPartner.state) && (
                    <span className="text-slate-600">
                      {" "}— Your earlier visit through{" "}
                      <strong>{linkedPartner.franchise_region || linkedPartner.state}</strong>
                      {linkedPartner.franchise_region && linkedPartner.state ? `, ${linkedPartner.state}` : ""}{" "}
                      is still attributed.
                    </span>
                  )}
                  <span className="text-slate-600">
                    {" "}Click your franchisee&apos;s link again any time to refresh the 90-day window.
                  </span>
                </>
              ) : (
                <>
                  <span className="font-semibold text-slate-900">
                    Booking via {linkedPartner.business_name}
                  </span>
                  {(linkedPartner.franchise_region || linkedPartner.state) ? (
                    <span className="text-slate-600">
                      {" "}— Your local Pristine Water Systems franchisee for{" "}
                      <strong>{linkedPartner.franchise_region || linkedPartner.state}</strong>
                      {linkedPartner.franchise_region && linkedPartner.state ? `, ${linkedPartner.state}` : ""}.
                      Same Aware Water Testing lab process — your franchisee will see your results.
                    </span>
                  ) : (
                    <span className="text-slate-600">
                      {" "}— Same Aware Water Testing lab process — your franchisee will see your results.
                    </span>
                  )}
                </>
              )}
              {/* Phase 25 — Customer Consent Wording. Pinned short
                  disclosure on every partner-attributed booking
                  banner. Calm, operational wording only. */}
              <div
                data-testid="ph25-attribution-banner-disclosure"
                className="mt-2 pt-2 border-t border-amber-200 text-[11px] text-slate-700 leading-snug"
              >
                Testing by Aware Water Testing. Local support by
                Pristine Water Systems
                {linkedPartner.franchise_region ? ` ${linkedPartner.franchise_region}` : (linkedPartner.state ? ` ${linkedPartner.state}` : "")}.
                The local PWS partner is not the laboratory. No service
                work is carried out without your approval. Recommendations
                are not quotes.
              </div>
              {sampleCollection && sampleCollection.available && sampleCollection.wording ? (
                <div
                  data-testid="ph30-sample-collection-banner"
                  className="mt-2 pt-2 border-t border-amber-200 text-[11px] text-slate-700 leading-snug"
                >
                  {sampleCollection.wording}
                </div>
              ) : null}
            </div>
          </div>
        )}
        <div data-testid={BOOKING.stepper} className="mb-10">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            {STEPS.map((s, i) => {
              const active = step === s.n;
              const done = step > s.n;
              return (
                <div key={s.n} className="flex-1 flex items-center">
                  <div className="flex flex-col items-start gap-2 flex-1">
                    <div className={`flex items-center gap-2.5 ${active ? "text-slate-900" : done ? "text-cyan-600" : "text-slate-400"}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                        done ? "bg-cyan-500 text-white" :
                        active ? "bg-slate-900 text-white" :
                        "bg-white border border-slate-200 text-slate-400"
                      }`}>
                        {done ? <Check className="w-4 h-4" /> : s.n}
                      </div>
                      <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wider">{s.label}</span>
                    </div>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`h-px flex-shrink-0 w-4 sm:w-8 ${done ? "bg-cyan-500" : "bg-slate-200"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-3xl border border-slate-200 p-7 lg:p-10 shadow-sm"
              >
                {step === 1 && (
                  <div>
                    {isMember && memberSavings > 0 && (
                      <div data-testid="booking-member-banner" className="mb-5 rounded-2xl bg-cyan-50 border border-cyan-200 p-3.5 flex items-center gap-2.5">
                        <Crown className="w-4 h-4 text-cyan-700 flex-shrink-0" />
                        <div className="text-sm text-cyan-900">
                          <span className="font-semibold">Member Price Applied</span>
                          <span className="text-cyan-700"> — your AWARE Water Health Record saves you a further ${memberSavings} on this test.</span>
                        </div>
                      </div>
                    )}
                    {isMember && memberSavings === 0 && (
                      <div data-testid="booking-member-banner" className="mb-5 rounded-2xl bg-cyan-50 border border-cyan-200 p-3.5 flex items-center gap-2.5">
                        <Crown className="w-4 h-4 text-cyan-700 flex-shrink-0" />
                        <div className="text-sm text-cyan-900">
                          <span className="font-semibold">AWARE Water Health Record active</span>
                          <span className="text-cyan-700"> — AI summaries, retest reminders, and historical archive included with this test.</span>
                        </div>
                      </div>
                    )}
                    {/* Phase 2J-AJ — replaced the passive $99/yr Membership
                        upsell link with an active in-flow Passport add-on
                        checkbox card rendered between the test selector
                        and the customer-details form. The upsell link is
                        intentionally removed per Stephen's spec — both
                        cannot coexist. */}
                    <h2 className="font-display text-2xl lg:text-3xl font-bold text-slate-900">Choose your test</h2>
                    <p className="mt-2 text-slate-600">Select the option that best matches your water source.</p>
                    <div className="mt-6 space-y-3">
                      {livePricedTests.map((t) => {
                        const Icon = t.icon;
                        const sel = selectedTest === t.id;
                        const partnerQ = partnerQuoteByTest[t.id];
                        // Feb 2026 — availability lookup. `available` is `true`
                        // by default (never seen / API not loaded yet).
                        const avail = availability[t.id] || {};
                        const disabled = avail.available === false;
                        // Phase 2L-A132C — PMs charge at RETAIL on the
                        // test selection cards too (was online_prepaid).
                        // The Order Summary on the right already does
                        // this; keep the cards in sync so PMs don't see
                        // a misleading "save 20%" / strike-through.
                        // Feb 2026 — also collapse to retail when the
                        // admin has disabled the central online discount.
                        const showPrice = partnerQ
                          ? Number(partnerQ.final_price)
                          : (isPropertyManager
                              ? t.retail
                              : (isMember
                                  ? Math.min(t.member, t.online_prepaid)
                                  : (discountEnabled ? t.online_prepaid : t.retail)));
                        const showCompareAt = !isPropertyManager && !partnerQ && discountEnabled && showStrikethrough && !isMember;
                        const compareAt = partnerQ
                          ? partnerQ.local_retail_baseline
                          : t.retail;
                        const priceLabel = partnerQ
                          ? "Partner price"
                          : (isPropertyManager
                              ? "Agency / account pricing"
                              : (isMember
                                  ? "Member"
                                  : (discountEnabled ? "Online prepaid" : "Standard price")));
                        // Format the available-again date without pulling in
                        // dayjs — the API always sends ISO YYYY-MM-DD.
                        let againShort = "";
                        if (disabled && avail.available_again_date) {
                          const [yy, mm, dd] = avail.available_again_date.split("-").map(Number);
                          const d = new Date(Date.UTC(yy, mm - 1, dd));
                          againShort = `${d.getUTCDate()} ${d.toLocaleString("en-AU", { month: "short", timeZone: "UTC" })}`;
                        }
                        return (
                          <button
                            key={t.id}
                            data-testid={TEST_TESTID[t.id]}
                            data-package-code={t.id}
                            data-availability={disabled ? "unavailable" : "available"}
                            onClick={() => { if (!disabled) setSelectedTest(t.id); }}
                            disabled={disabled}
                            aria-disabled={disabled || undefined}
                            className={`w-full text-left p-5 rounded-2xl border-2 transition-all flex items-center gap-4 ${
                              disabled
                                ? "border-slate-200 bg-slate-50 opacity-70 cursor-not-allowed"
                                : sel
                                  ? "border-cyan-500 bg-cyan-50/40 shadow-md"
                                  : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                          >
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${disabled ? "bg-slate-200 text-slate-400" : sel ? "bg-cyan-500 text-white" : "bg-slate-100 text-slate-600"}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className={`font-display font-semibold ${disabled ? "text-slate-500" : "text-slate-900"}`}>{t.name}</h3>
                                {t.featured && !disabled && <span className="text-[9px] font-bold uppercase tracking-wider bg-cyan-500 text-white px-1.5 py-0.5 rounded">Popular</span>}
                                {disabled && (
                                  <span
                                    data-testid={`booking-package-unavailable-badge-${t.id}`}
                                    className="text-[9px] font-bold uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded"
                                  >
                                    Temporarily unavailable
                                  </span>
                                )}
                              </div>
                              <p className={`text-sm mt-0.5 ${disabled ? "text-slate-400" : "text-slate-500"}`}>
                                {disabled
                                  ? (avail.customer_message || "This test is temporarily unavailable.")
                                  : t.tagline}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              {disabled ? (
                                <div
                                  data-testid={`booking-package-again-${t.id}`}
                                  className="text-xs font-semibold text-amber-700 whitespace-nowrap"
                                >
                                  {againShort ? `Available again ${againShort}` : "Temporarily unavailable"}
                                </div>
                              ) : (
                                <>
                                  <div className="font-display font-bold text-slate-900 text-xl">${showPrice}</div>
                                  {showCompareAt && (
                                    <div className="text-xs text-slate-400 line-through">${compareAt}</div>
                                  )}
                                  <div className="text-[9px] font-bold uppercase tracking-wider text-cyan-700 mt-0.5">{priceLabel}</div>
                                </>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Feb 2026 — AWARE Water Health Record paid add-on RETIRED.
                        Replaced with an informational panel that confirms
                        the free AWARE Water Health Record inclusion.
                        Property Manager context still hides the panel
                        because their portal already surfaces the record. */}
                    {!isPropertyManager && (
                      <div
                        className="mt-7 rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50/60 to-sky-50/60 p-5"
                        data-testid="booking-water-health-record-panel"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-cyan-600 text-white flex items-center justify-center flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618 4.016A11.955 11.955 0 0112 21.75a11.955 11.955 0 01-8.618-3.734A12.02 12.02 0 013 9.75c0-1.605.377-3.132 1.041-4.5A11.955 11.955 0 0112 2.25c3.06 0 5.86 1.145 7.959 3.032A11.955 11.955 0 0121 9.75c0 1.605-.377 3.132-1.041 4.5A11.955 11.955 0 0121 14.016z" />
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-bold uppercase tracking-wider text-cyan-700 mb-1">
                              Included at no extra cost
                            </div>
                            <div className="font-display text-lg font-bold text-slate-900 mb-2">
                              Your AWARE Water Health Record is included
                            </div>
                            <p className="text-sm text-slate-700 leading-relaxed">
                              Every completed AWARE water test starts or updates your property&apos;s Water Health Record — keeping your reports, testing history and recommended retest dates together in one place.
                            </p>
                            <p className="text-sm text-slate-700 leading-relaxed mt-2">
                              Complete testing at the recommended interval to keep your property&apos;s <span className="font-semibold">AWARE Water Health Record</span> current.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Phase 2L-A80 — Invite-a-Neighbour voucher banner.
                        Only renders when the customer arrived via
                        ?invite=<token>. Green when the advisory lookup
                        validated; amber when the token is known but no
                        longer usable. Server re-validates at checkout
                        so this banner is purely informational.
                        Phase 2L-A117 — never shown to Property Managers
                        (consumer-only promotion). */}
                    {initialInviteToken && voucher?.valid && !isPropertyManager && (
                      <div
                        data-testid="invite-voucher-applied"
                        className="mt-7 rounded-xl bg-emerald-50 border border-emerald-200 p-4"
                      >
                        <div className="text-[10px] tracking-[0.2em] font-bold uppercase text-emerald-700">
                          Invite-a-Neighbour voucher applied
                        </div>
                        <div className="mt-1 text-lg font-semibold text-emerald-900">
                          ${voucher.amount} off your first Aware water test
                        </div>
                        <div className="mt-1 text-xs font-mono text-emerald-900">
                          Code: {voucher.voucher_code}
                        </div>
                        <div className="mt-1 text-xs text-emerald-700">
                          We&apos;ll apply this automatically at checkout — no code entry needed.
                        </div>
                      </div>
                    )}
                    {initialInviteToken && !voucher?.valid && voucherErr && (
                      <div
                        data-testid="invite-voucher-error"
                        className="mt-7 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900"
                      >
                        {voucherErr} You can still continue with the regular price.
                      </div>
                    )}

                    <div className="mt-7 flex justify-end">
                      <Button data-testid={BOOKING.step1Next} onClick={next} size="lg" className="btn-aqua rounded-full font-semibold">
                        Continue <ArrowRight className="w-4 h-4 ml-1.5" />
                      </Button>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    {/* Phase 2L-A121.B — PM-aware Step 2: Booking authority
                        banner + locked property card + tenant/site contact
                        form replacing "Your Details". Activated only when
                        the PM arrived via ?property=<id> on a property in
                        their portfolio (pmContextActive). Standard
                        customers / unlinked PMs see the unchanged retail
                        flow below. */}
                    {pmContextActive ? (
                      <>
                        <div
                          data-testid="booking-pm-authority-banner"
                          className="mb-6 rounded-2xl bg-cyan-50 border border-cyan-200 p-4 flex items-start gap-3"
                        >
                          <ShieldCheck className="w-5 h-5 mt-0.5 text-cyan-700 flex-shrink-0" />
                          <div className="text-sm">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700">
                              Booking authority
                            </div>
                            <div
                              data-testid="booking-pm-authority-agency"
                              className="font-display font-semibold text-slate-900 text-base mt-0.5"
                            >
                              {pmPartner?.business_name || pmPartner?.email}
                            </div>
                            <div className="text-xs text-slate-600 mt-0.5">
                              {[pmPartner?.contact_name, pmPartner?.email, pmPartner?.phone].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                        </div>

                        <h2 className="font-display text-2xl lg:text-3xl font-bold text-slate-900">Tenant / site contact details</h2>
                        <p className="mt-2 text-slate-600">
                          We&apos;re booking on a property you manage. Provide the
                          tenant or on-site contact so our lab knows who to
                          call about sample drop-off or site access — the
                          invoice still goes to your agency.
                        </p>

                        {/* Locked property card */}
                        <div
                          data-testid="booking-locked-property"
                          className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-5"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
                                Selected managed property
                              </div>
                              <div
                                data-testid="booking-locked-property-address"
                                className="font-display font-semibold text-slate-900 mt-1"
                              >
                                {lockedProperty?.address || "—"}
                              </div>
                              <div className="text-xs text-slate-500 mt-0.5">
                                {[lockedProperty?.suburb, lockedProperty?.postcode].filter(Boolean).join(" · ") || "—"}
                                {lockedProperty?.sources?.[0]?.type && (
                                  <> · {lockedProperty.sources[0].type.replace("_", " ")}</>
                                )}
                              </div>
                            </div>
                            <span
                              data-testid="booking-locked-property-lock"
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-white border border-slate-200 rounded-full px-2.5 py-1"
                            >
                              <Lock className="w-3 h-3" /> Locked
                            </span>
                          </div>
                          <p className="mt-3 text-[11px] text-slate-500">
                            Address is read-only to prevent accidental
                            duplicates. To book a different property,{" "}
                            <Link to="/property-manager" className="text-cyan-700 font-semibold hover:underline">
                              return to your portfolio
                            </Link>
                            .
                          </p>
                        </div>

                        {/* Tenant / Site Contact form */}
                        <div className="mt-6 grid sm:grid-cols-2 gap-4">
                          <div className="sm:col-span-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                              Tenant / site contact name <span className="text-rose-600">*</span>
                            </Label>
                            <Input
                              data-testid="booking-site-contact-name"
                              value={siteContact.name}
                              onChange={(e) => setSiteContact((s) => ({ ...s, name: e.target.value }))}
                              placeholder="e.g. Tina Tenant"
                              className="mt-1.5 h-12 rounded-xl"
                              maxLength={200}
                            />
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                              Phone <span className="text-rose-600">*</span>
                            </Label>
                            <Input
                              data-testid="booking-site-contact-phone"
                              value={siteContact.phone}
                              onChange={(e) => setSiteContact((s) => ({ ...s, phone: e.target.value }))}
                              placeholder="04XX XXX XXX"
                              className="mt-1.5 h-12 rounded-xl"
                              maxLength={40}
                            />
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                              Email <span className="text-slate-400 normal-case lowercase tracking-normal">(optional)</span>
                            </Label>
                            <Input
                              data-testid="booking-site-contact-email"
                              type="email"
                              value={siteContact.email}
                              onChange={(e) => setSiteContact((s) => ({ ...s, email: e.target.value }))}
                              placeholder="tenant@example.com"
                              className="mt-1.5 h-12 rounded-xl"
                              maxLength={200}
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                              Access notes <span className="text-slate-400 normal-case lowercase tracking-normal">(optional)</span>
                            </Label>
                            <textarea
                              data-testid="booking-site-contact-access-notes"
                              value={siteContact.access_notes}
                              onChange={(e) => setSiteContact((s) => ({ ...s, access_notes: e.target.value }))}
                              placeholder="Gate code · pet on-site · best time to call · tank/bore location…"
                              rows={3}
                              maxLength={2000}
                              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 resize-y"
                            />
                          </div>
                        </div>

                        {/* PO number captured in Step 2 for PMs.
                            Required only when payment method ends up
                            being Invoice (validated in Step 3 again).
                            Optional here so a PM choosing Stripe can
                            skip it; keeping the field visible avoids
                            a context switch in Step 3. */}
                        <div className="mt-6 p-4 rounded-2xl bg-white ring-1 ring-slate-200">
                          <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                            Purchase Order number <span className="text-slate-400 normal-case lowercase tracking-normal">(required for invoice; optional for pay-online)</span>
                          </Label>
                          <Input
                            data-testid="booking-po-number-step2"
                            value={purchaseOrderNumber}
                            onChange={(e) => setPurchaseOrderNumber(e.target.value)}
                            placeholder="e.g. PO-2026-0814"
                            className="mt-1.5 h-11 rounded-xl font-mono"
                            maxLength={80}
                          />
                          <p className="mt-2 text-[11px] text-slate-500">
                            If you pay by invoice, this PO will appear on the invoice emailed to <strong>{pmPartner?.email}</strong>.
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <h2 className="font-display text-2xl lg:text-3xl font-bold text-slate-900">Your details</h2>
                        <p className="mt-2 text-slate-600">We&apos;ll send your reports and sample instructions here.</p>
                        <div className="mt-6 grid sm:grid-cols-2 gap-4">
                          <div className="sm:col-span-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Full Name</Label>
                            <Input data-testid={BOOKING.inputName} value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} placeholder="Jane Doe" className="mt-1.5 h-12 rounded-xl" />
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Mobile</Label>
                            <Input data-testid={BOOKING.inputMobile} value={details.mobile} onChange={(e) => setDetails({ ...details, mobile: e.target.value })} placeholder="04XX XXX XXX" className="mt-1.5 h-12 rounded-xl" />
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Email</Label>
                            <Input data-testid={BOOKING.inputEmail} type="email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} placeholder="you@example.com" className="mt-1.5 h-12 rounded-xl" />
                          </div>
                          <div className="sm:col-span-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Property Address</Label>
                            <AddressAutocomplete
                              testId={BOOKING.inputAddress}
                              value={details.address}
                              onChange={(v) => setDetails((d) => ({ ...d, address: v }))}
                              placeholder="123 Coastal Road, Noosa QLD"
                            />
                            {/* Aware Feb-2026 — the "Check we service
                                this address" widget was removed from
                                the booking flow. Aware operates as a
                                national posted-kit service, so the
                                customer address is captured normally
                                and internal partner allocation
                                (region_allocation_service) runs on
                                the backend without ever blocking. */}
                          </div>

                          {/* Phase 2L-A31 — optional sample identification fields. The
                              previous design wrapped these in a grey slate-50 card which
                              made them read as disabled/inactive. Switched to a white
                              surface with a subtle ring so they look like normal
                              editable inputs while still being visually grouped. */}
                          <div className="sm:col-span-2 mt-2 p-4 rounded-2xl bg-white ring-1 ring-slate-200">
                            <div className="text-xs uppercase tracking-wider font-semibold text-slate-700 mb-1">Sample identification <span className="text-slate-400 normal-case lowercase tracking-normal">(optional)</span></div>
                            <p className="text-[12px] text-slate-500 mb-3">If you&apos;re booking more than one test, give each sample a label so you can tell them apart on your report.</p>
                            <div className="grid sm:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Customer sample reference</Label>
                                <Input
                                  data-testid="booking-customer-sample-reference"
                                  value={customerSampleReference}
                                  onChange={(e) => setCustomerSampleReference(e.target.value)}
                                  placeholder="e.g. Tank A, Unit 2, Bore near shed"
                                  maxLength={80}
                                  className="mt-1 h-11 rounded-xl bg-white"
                                />
                              </div>
                              <div>
                                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Where was this sample taken from?</Label>
                                <Input
                                  data-testid="booking-sample-point"
                                  value={samplePoint}
                                  onChange={(e) => setSamplePoint(e.target.value)}
                                  placeholder="e.g. Kitchen tap, Tank outlet, Post-filter"
                                  maxLength={120}
                                  className="mt-1 h-11 rounded-xl bg-white"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Phase 8 + Phase 2L-A144 (Feb 2026) — the entire
                              Sample Container picker is now gated on the
                              `posted_kit_paid` feature flag. When the flag
                              is DISABLED (default), the picker does NOT
                              render at all — the server silently defaults
                              `kit_method="mail_out"`, which restores the
                              pre-Phase-8 customer-facing UX. This was a
                              production-hotfix on 2026-07-02 after the
                              A144 deploy exposed the previously ungated
                              Phase 8 picker to the public. When the flag
                              is INTERNAL_ONLY (admin/lab_technician
                              preview) or ENABLED (public rollout), the
                              new 2-option paid layout renders. The
                              legacy 3-chip picker is now admin-only:
                              gated behind the same visibility check but
                              routed to the new UI regardless. */}
                          {(() => {
                            // Feb 2026 — if ≤1 option would render, skip the picker.
                            // `visibleKitOptions` is the single source of truth (see
                            // the memo near the top of this component). Exception:
                            // a mail-out-only flow still needs its section rendered
                            // because the postal address form lives inside it.
                            if (visibleKitOptions.length <= 1
                                && visibleKitOptions[0] !== "mail_out") {
                              // Feb 2026 — friendly out-of-stock notice.
                              // When the partner has kit-supplied ENABLED
                              // but is currently at 0 stock, `reason` comes
                              // back as `out_of_stock`. If Mail-Out is the
                              // only remaining option we tell the customer
                              // WHY — otherwise the mail-out fee just
                              // materialises on the Order Summary with no
                              // explanation. Auto-select still fires from
                              // the useEffect above so the booking proceeds.
                              const showOosNotice =
                                partnerKitOptions.reason === "out_of_stock"
                                && visibleKitOptions.includes("mail_out")
                                && !!partnerKitOptions.partner;
                              if (showOosNotice) {
                                const feeDollars = ((postedKit.fee_cents || 0) / 100).toFixed(2);
                                return (
                                  <div
                                    className="sm:col-span-2 mt-2 p-4 rounded-2xl bg-amber-50 ring-1 ring-amber-200"
                                    data-testid="booking-partner-out-of-stock-notice"
                                  >
                                    <div className="flex items-start gap-2.5">
                                      <Info className="w-5 h-5 mt-0.5 text-amber-700 flex-shrink-0" />
                                      <div className="text-[13px] text-amber-900 leading-relaxed">
                                        <span className="block font-semibold text-amber-950 mb-0.5">
                                          Your local partner is currently out of kits
                                        </span>
                                        {partnerKitOptions.partner.business_name || "Your local Pristine Water Systems Partner"}
                                        {localLab.enabled ? (
                                          <>
                                            {" "}has run out of sample kits right now, so AWARE will post your kit
                                            directly to you instead —{" "}
                                            <strong className="text-amber-950">postage included</strong>, at no
                                            extra cost on your booking total.
                                          </>
                                        ) : (
                                          <>
                                            {" "}has run out of sample kits right now, so AWARE will post your kit directly to you for{" "}
                                            <strong className="text-amber-950">${feeDollars}</strong>
                                            {" "}(added to your booking total). Mail-out kits are charged at the same price as partner-supplied kits, so you&rsquo;re not paying more.
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }
                            return (
                          <div
                            className="sm:col-span-2 mt-2 p-4 rounded-2xl bg-white ring-1 ring-slate-200"
                            data-testid="booking-posted-kit-section"
                          >
                            {postedKitFlagStatus === "internal_only" && (
                              <div
                                data-testid="booking-posted-kit-internal-banner"
                                className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-start gap-2"
                              >
                                <ShieldCheck className="w-4 h-4 mt-0.5 text-amber-700 flex-shrink-0" />
                                <div className="text-[12px] leading-snug text-amber-900">
                                  <span className="font-bold uppercase tracking-wider text-[10px] block">Admin preview</span>
                                  Only admins can see this section — publicly hidden.
                                  Flip to &ldquo;Public&rdquo; on{" "}
                                  <a href="/admin/settings/posted-kit" className="underline font-semibold hover:text-amber-950">
                                    Posted Kit Settings
                                  </a>{" "}
                                  to launch to customers.
                                </div>
                              </div>
                            )}
                            <div className="flex items-center gap-2 mb-1">
                              <div className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                                How would you like to receive your sample kit?
                              </div>
                            </div>
                            <p className="text-[12px] text-slate-500 mb-3">
                              {localLab.enabled ? (
                                <>Postage is included in your package price. Local customers save <strong>$20</strong> by using a lab option.</>
                              ) : (
                                <>Choose how you&apos;d like your sample kit. Lab pickup is free; posted kits attach a small postage &amp; packaging fee that is shown separately at checkout.</>
                              )}
                            </p>
                            <div className="grid sm:grid-cols-3 gap-2.5">
                              {(() => {
                                // Feb 2026 — All-Inclusive Pricing:
                                // the two public lab options are gated by
                                // postcode eligibility (see the
                                // `visibleKitOptions` memo — the single
                                // source of truth). This gate is UX only —
                                // the server re-validates the discount
                                // claim on /payments/checkout.
                                const savingCents = localLab.discountCents
                                  || localLab.configuredDiscountCents;
                                const savingBadge = localLab.enabled && savingCents > 0
                                  ? `$${(savingCents / 100).toFixed(0)} OFF`
                                  : "Free";
                                const savingSubline = localLab.enabled && savingCents > 0
                                  ? (localLab.eligible
                                      ? "Applied at checkout"
                                      : "Local Sunshine Coast / Noosa addresses")
                                  : null;
                                const opts = [
                                  {
                                    key: "local_pickup_dropoff",
                                    label: "Pick up sample bottle from AWARE laboratory",
                                    desc: "Collect a sample bottle from Unit 1/37 Gateway Drive, Noosaville, then return your sample to the lab.",
                                    badge: savingBadge,
                                    subline: savingSubline,
                                  },
                                  {
                                    key: "existing_bottle",
                                    label: "I'll drop off my own sample bottle",
                                    desc: "Use a clean 500 mL bottle and drop your sample to Unit 1/37 Gateway Drive, Noosaville.",
                                    badge: savingBadge,
                                    subline: savingSubline,
                                  },
                                  {
                                    key: "mail_out",
                                    label: "Mail-Out Sample Kit",
                                    desc: "AWARE will send the sample kit directly to your nominated address. Postage to you and return to the lab is included — no extra cost.",
                                    // Feb 2026 — under the All-Inclusive Pricing rollout,
                                    // postage is baked into the package sticker price.
                                    // The mail-out badge reads "FREE POSTAGE" instead of
                                    // the dollar amount so customers don't think they'll
                                    // be charged twice. Legacy pre-rollout bookings still
                                    // show the numeric fee.
                                    badge: localLab.enabled
                                      ? "FREE POSTAGE"
                                      : `$${((postedKit.fee_cents || 0) / 100).toFixed(2)}`,
                                  },
                                  {
                                    key: "partner_supplied",
                                    label: partnerKitOptions.available
                                      ? `Already provided by my local PWS Partner${partnerKitOptions.partner?.business_name ? ` (${partnerKitOptions.partner.business_name})` : ""}`
                                      : "I already have a kit from a PWS Partner",
                                    desc: partnerKitOptions.available
                                      ? "Select this option when your local Pristine Water Systems Partner has already provided your sample kit. The kit includes prepaid Express Post return packaging."
                                      : "Select this if a Pristine Water Systems Partner has already given you a sample kit — you'll choose which Partner next. The kit includes prepaid Express Post return packaging.",
                                    badge: (partnerKitOptions.available
                                      ? partnerKitOptions.customer_charge_cents
                                      : supplyingPartners.chargeCents) > 0
                                      ? `$${((partnerKitOptions.available
                                          ? partnerKitOptions.customer_charge_cents
                                          : supplyingPartners.chargeCents) / 100).toFixed(2)}`
                                      : "Free",
                                  },
                                ].filter(o => visibleKitOptions.includes(o.key));
                                // Feb 2026 — when all 4 options are visible, the 4th
                                // ("partner_supplied") wraps to a second row on its own.
                                // Rather than leave two empty columns, we stretch it
                                // full-width AND flip its inner layout to horizontal
                                // so it feels balanced under the top row.
                                const fullWidthPartnerRow = opts.length === 4;
                                return opts.map(opt => {
                                  const isPartnerFullRow =
                                    fullWidthPartnerRow && opt.key === "partner_supplied";
                                  return (
                                    <button
                                      type="button"
                                      key={opt.key}
                                      onClick={() => setKitMethod(opt.key)}
                                      data-testid={`booking-posted-kit-option-${opt.key}`}
                                      className={`text-left rounded-xl border p-3 transition-all ${
                                        isPartnerFullRow ? "sm:col-span-3" : ""
                                      } ${
                                        kitMethod === opt.key
                                          ? "border-cyan-600 bg-cyan-50 ring-2 ring-cyan-600/20"
                                          : "border-slate-200 bg-white hover:border-slate-300"
                                      }`}
                                    >
                                      {isPartnerFullRow ? (
                                        <div className="flex items-start gap-3">
                                          <div className={`mt-1 w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${kitMethod === opt.key ? "bg-cyan-600 border-cyan-600" : "border-slate-300"}`} />
                                          <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-4">
                                            <div className="min-w-0 sm:flex-1">
                                              <div className="font-semibold text-[13px] text-slate-900 leading-snug break-words">{opt.label}</div>
                                              <div className="mt-1 text-[11px] text-slate-500 leading-snug break-words">{opt.desc}</div>
                                            </div>
                                            <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-cyan-100 text-cyan-800 self-start sm:self-center flex-shrink-0 mt-1 sm:mt-0">
                                              {opt.badge}
                                            </span>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="flex items-start gap-2">
                                          <div className={`mt-1 w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${kitMethod === opt.key ? "bg-cyan-600 border-cyan-600" : "border-slate-300"}`} />
                                          <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-[13px] text-slate-900 leading-snug break-words">{opt.label}</div>
                                            <span className={`inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${opt.key === "mail_out" || opt.key === "partner_supplied" ? "bg-cyan-100 text-cyan-800" : "bg-emerald-100 text-emerald-800"}`}>{opt.badge}</span>
                                            {opt.subline && (
                                              <div
                                                className="mt-1 text-[10px] font-semibold text-emerald-700 leading-snug"
                                                data-testid={`booking-posted-kit-subline-${opt.key}`}
                                              >
                                                {opt.subline}
                                              </div>
                                            )}
                                            <div className="mt-1.5 text-[11px] text-slate-500 leading-snug break-words">{opt.desc}</div>
                                          </div>
                                        </div>
                                      )}
                                    </button>
                                  );
                                });
                              })()}
                            </div>

                            {/* Partner-supplied detail card (Feb 2026) */}
                            {kitMethod === "partner_supplied" && partnerKitOptions.available && (
                              <div
                                className="mt-3 p-3 rounded-xl bg-cyan-50 border border-cyan-200"
                                data-testid="booking-partner-supplied-detail"
                              >
                                <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-800 mb-1">
                                  Kit supplied by your PWS Partner
                                </div>
                                <div className="text-[13px] font-semibold text-slate-900">
                                  {partnerKitOptions.partner?.business_name || "Your local Pristine Water Systems Partner"}
                                </div>
                                <div className="mt-1.5 text-[12px] text-slate-700 leading-snug">
                                  Your Partner will hand over a ready-to-use sample kit including a prepaid Express Post return label to the Aware Noosaville lab. No AWARE-posted kit will be sent for this booking.
                                </div>
                                <div className="mt-2 text-[11px] font-semibold text-cyan-900 tabular-nums">
                                  Kit charge: ${(partnerKitOptions.customer_charge_cents / 100).toFixed(2)} · shown as a separate line at checkout
                                </div>
                              </div>
                            )}

                            {/* Jun 2026 — direct booking: pick the supplying Partner */}
                            {kitMethod === "partner_supplied" && directPartnerKitAvailable && (
                              <div
                                className="mt-3 p-3 rounded-xl bg-cyan-50 border border-cyan-200"
                                data-testid="booking-partner-supplied-picker"
                              >
                                <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-800 mb-1">
                                  Which Partner gave you the kit?
                                </div>
                                <div className="mt-1.5 text-[12px] text-slate-700 leading-snug">
                                  Choose the Pristine Water Systems Partner who supplied your sample kit. Your kit already includes a prepaid Express Post return label to the Aware Noosaville lab — no AWARE-posted kit will be sent for this booking.
                                </div>
                                <select
                                  value={selectedSupplyingPartnerId}
                                  onChange={(e) => setSelectedSupplyingPartnerId(e.target.value)}
                                  data-testid="booking-supplying-partner-select"
                                  className="mt-2.5 w-full rounded-xl border border-cyan-300 bg-white px-3 py-2.5 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-cyan-600/30"
                                >
                                  <option value="">Select your PWS Partner…</option>
                                  {supplyingPartners.rows.map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.business_name}
                                      {p.state ? ` — ${p.state}` : ""}
                                    </option>
                                  ))}
                                </select>
                                {!selectedSupplyingPartnerId && (
                                  <div
                                    className="mt-2 text-[11px] font-semibold text-amber-800"
                                    data-testid="booking-supplying-partner-required"
                                  >
                                    Please choose your Partner to continue with this option.
                                  </div>
                                )}
                                <div className="mt-2 text-[11px] font-semibold text-cyan-900 tabular-nums">
                                  Kit charge: ${(supplyingPartners.chargeCents / 100).toFixed(2)}
                                  {supplyingPartners.chargeCents > 0 ? " · shown as a separate line at checkout" : " · included in your package price"}
                                </div>
                              </div>
                            )}


                            {/* Own-bottle caution + drop-off detail card */}
                            {kitMethod === "existing_bottle" && (
                              <div
                                className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200"
                                data-testid="booking-posted-kit-own-bottle"
                              >
                                <div className="text-[11px] uppercase tracking-wider font-bold text-amber-800 mb-1">
                                  Bring your own clean 500 mL bottle
                                </div>
                                <div className="text-[13px] font-semibold text-slate-900">
                                  Drop off at Aware Water Testing · Unit 1/37 Gateway Drive, Noosaville QLD 4566
                                </div>
                                <div className="mt-1.5 text-[12px] text-amber-900 leading-snug">
                                  Some tests may require a lab-supplied bottle. We&apos;ll contact you if your selected test requires a specific container.
                                </div>
                              </div>
                            )}

                            {/* Lab-pickup detail card */}
                            {kitMethod === "local_pickup_dropoff" && (
                              <div
                                className="mt-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200"
                                data-testid="booking-posted-kit-lab-pickup"
                              >
                                <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-1">
                                  Collect from our Noosaville lab
                                </div>
                                <div className="text-[13px] font-semibold text-slate-900">
                                  {postedKit.lab_pickup_address || "Aware Water Testing · Unit 1/37 Gateway Drive, Noosaville QLD 4566"}
                                </div>
                                <div className="mt-1 text-[12px] text-slate-700">
                                  There is no postage charge for this option.
                                </div>
                              </div>
                            )}

                            {/* Post-a-kit detail card — image + contents + address form */}
                            {kitMethod === "mail_out" && (
                              <div
                                className="mt-3 rounded-xl border border-cyan-200 bg-cyan-50/40 p-3 sm:p-4 space-y-3"
                                data-testid="booking-posted-kit-mail-out"
                              >
                                {/* Kit image + contents */}
                                <div className="flex flex-col sm:flex-row gap-3">
                                  <div
                                    className="w-full sm:w-40 h-32 rounded-lg overflow-hidden bg-white border border-cyan-200 flex items-center justify-center flex-shrink-0"
                                    data-testid="booking-posted-kit-image"
                                  >
                                    {postedKit.image_url ? (
                                      <ZoomableImage
                                        src={
                                          // Absolute URLs pass through; relative
                                          // /api/uploads/... paths returned by the
                                          // admin uploader are resolved against
                                          // the backend host so the customer's
                                          // browser fetches the file from the
                                          // FastAPI static mount at
                                          // /api/uploads/posted_kit/*.
                                          postedKit.image_url.startsWith("/")
                                            ? `${process.env.REACT_APP_BACKEND_URL}${postedKit.image_url}`
                                            : postedKit.image_url
                                        }
                                        alt="Example water testing kit contents"
                                        caption={postedKit.image_caption || "Your posted kit — hover to magnify, click to expand"}
                                        className="w-full h-full"
                                        imgClassName="w-full h-full object-cover"
                                        testId="booking-posted-kit-image-zoom"
                                      />
                                    ) : (
                                      <div className="text-center px-3">
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-700">Example of kit contents</div>
                                        <div className="mt-1 text-[11px] text-slate-500 leading-snug">
                                          Kit image — replace via admin setting
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 text-[12px] text-slate-700 leading-relaxed">
                                    <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-700 mb-1">
                                      What you will receive
                                    </div>
                                    {postedKit.image_caption && (
                                      <p className="mb-2">{postedKit.image_caption}</p>
                                    )}
                                    <ul className="list-disc list-inside space-y-0.5 text-[12px]">
                                      <li>Sterile sample bottle</li>
                                      <li>Step-by-step collection instructions</li>
                                      <li>Return packaging</li>
                                      <li>Prepaid Express Post return label</li>
                                    </ul>
                                  </div>
                                </div>

                                {/* Postal address form */}
                                <div className="pt-3 border-t border-cyan-200/60">
                                  <div className="text-[11px] uppercase tracking-wider font-bold text-slate-700 mb-2">
                                    Where should we post the kit?
                                  </div>

                                  {/* Feb-2026 bugfix — source toggle so the
                                      customer can reuse the property address
                                      from Step 2 rather than retyping. */}
                                  <div
                                    className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3"
                                    data-testid="booking-postal-source-toggle"
                                  >
                                    {[
                                      {
                                        key: "property",
                                        label: "Use the property / sample address above",
                                        desc: "Reuses the address you entered earlier.",
                                      },
                                      {
                                        key: "custom",
                                        label: "Enter a different postal address",
                                        desc: "Search or type a separate delivery address.",
                                      },
                                    ].map((opt) => (
                                      <button
                                        type="button"
                                        key={opt.key}
                                        onClick={() => setPostalAddressSource(opt.key)}
                                        data-testid={`booking-postal-source-${opt.key}`}
                                        className={`text-left rounded-xl border p-2.5 transition-all ${
                                          postalAddressSource === opt.key
                                            ? "border-cyan-600 bg-cyan-50 ring-2 ring-cyan-600/20"
                                            : "border-slate-200 bg-white hover:border-slate-300"
                                        }`}
                                      >
                                        <div className="flex items-start gap-2">
                                          <div className={`mt-1 w-3 h-3 rounded-full border-2 flex-shrink-0 ${postalAddressSource === opt.key ? "bg-cyan-600 border-cyan-600" : "border-slate-300"}`} />
                                          <div className="flex-1">
                                            <div className="font-semibold text-[12px] text-slate-900 leading-snug">{opt.label}</div>
                                            <div className="mt-0.5 text-[11px] text-slate-500 leading-snug">{opt.desc}</div>
                                          </div>
                                        </div>
                                      </button>
                                    ))}
                                  </div>

                                  {/* "Use property address" — pre-filled from
                                      the property address, but ALWAYS
                                      editable (Nov 2026, SCWT-34E6D6): a
                                      read-only preview let a bad parse (or a
                                      comma-less address) slip through to
                                      checkout unnoticed. Now the customer
                                      reviews the SAME structured fields used
                                      for "custom", pre-filled for convenience,
                                      and must fix anything wrong before
                                      Continue is enabled. */}
                                  {postalAddressSource === "property" && !details.address.trim() && (
                                    <div className="mb-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-700">
                                      No property address entered yet. Please enter your address in the
                                      &ldquo;Property Address&rdquo; field above, or choose
                                      &ldquo;Enter a different postal address&rdquo;.
                                    </div>
                                  )}

                                  {/* Shared editable address fields — used by
                                      BOTH "property" (pre-filled, editable)
                                      and "custom" (autocomplete, editable)
                                      sources, so every mail-out customer
                                      reviews real street/suburb/state/postcode
                                      fields before paying — not a hidden
                                      derived string. */}
                                  <div className="grid sm:grid-cols-2 gap-2.5" data-testid="booking-postal-address-fields">
                                    <div className="sm:col-span-2">
                                      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Street address</Label>
                                      {postalAddressSource === "custom" ? (
                                        <AddressAutocomplete
                                          testId="booking-posted-kit-line1"
                                          value={postedKitAddress.line1}
                                          onChange={(v) => {
                                            // AddressAutocomplete emits a
                                            // free-form string whether the
                                            // customer types it (comma-less or
                                            // not — autocomplete SELECTION is
                                            // never required) or picks a
                                            // Google suggestion. parseAusAddress
                                            // splits it into structured fields;
                                            // every field stays editable below
                                            // so a wrong split can be fixed by
                                            // hand instead of forcing a retype.
                                            setPostalAddressAddressFromAutocomplete(v, setPostedKitAddress);
                                          }}
                                          onPlace={(place) => {
                                            // A real Google suggestion was
                                            // picked — use its authoritative
                                            // structured components instead of
                                            // our own regex guess.
                                            setPostedKitAddress((prev) => ({
                                              ...prev,
                                              line1: place.street_address || prev.line1,
                                              suburb: place.suburb || prev.suburb,
                                              state: place.state || prev.state,
                                              postcode: place.postcode || prev.postcode,
                                              country: "Australia",
                                            }));
                                          }}
                                          onBlur={splitPostedKitLine1OnBlur}
                                          placeholder="Start typing your postal address…"
                                        />
                                      ) : (
                                        <Input
                                          data-testid="booking-posted-kit-line1"
                                          value={postedKitAddress.line1}
                                          onChange={(e) => setPostedKitAddress(a => ({ ...a, line1: e.target.value }))}
                                          placeholder="174 Hohenhaus Rd"
                                          maxLength={200}
                                          className="mt-1 h-11 rounded-xl bg-white"
                                        />
                                      )}
                                    </div>
                                    <div className="sm:col-span-2">
                                      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Address line 2 <span className="normal-case text-slate-400 tracking-normal">(optional)</span></Label>
                                      <Input
                                        data-testid="booking-posted-kit-line2"
                                        value={postedKitAddress.line2}
                                        onChange={(e) => setPostedKitAddress(a => ({ ...a, line2: e.target.value }))}
                                        placeholder="Unit / building"
                                        maxLength={200}
                                        className="mt-1 h-11 rounded-xl bg-white"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Suburb</Label>
                                      <Input
                                        data-testid="booking-posted-kit-suburb"
                                        value={postedKitAddress.suburb}
                                        onChange={(e) => setPostedKitAddress(a => ({ ...a, suburb: e.target.value }))}
                                        maxLength={120}
                                        className="mt-1 h-11 rounded-xl bg-white"
                                      />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2.5">
                                      <div>
                                        <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">State</Label>
                                        <Input
                                          data-testid="booking-posted-kit-state"
                                          value={postedKitAddress.state}
                                          onChange={(e) => setPostedKitAddress(a => ({ ...a, state: e.target.value.toUpperCase() }))}
                                          placeholder="QLD"
                                          maxLength={10}
                                          className="mt-1 h-11 rounded-xl bg-white"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Postcode</Label>
                                        <Input
                                          data-testid="booking-posted-kit-postcode"
                                          value={postedKitAddress.postcode}
                                          onChange={(e) => setPostedKitAddress(a => ({ ...a, postcode: e.target.value }))}
                                          placeholder="4566"
                                          maxLength={10}
                                          className="mt-1 h-11 rounded-xl bg-white"
                                        />
                                      </div>
                                    </div>
                                    <div className="sm:col-span-2">
                                      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Delivery notes <span className="normal-case text-slate-400 tracking-normal">(optional)</span></Label>
                                      <Input
                                        data-testid="booking-posted-kit-delivery-notes"
                                        value={postedKitDeliveryNotes}
                                        onChange={(e) => setPostedKitDeliveryNotes(e.target.value)}
                                        placeholder="e.g. leave at back door"
                                        maxLength={200}
                                        className="mt-1 h-11 rounded-xl bg-white"
                                      />
                                    </div>
                                  </div>
                                </div>

                                {/* Live checkout preview — this is what the customer
                                    will see itemised on the Stripe hosted page.
                                    Test price is stubbed from the selected test's
                                    retail; server always recomputes. */}
                                <div
                                  className="pt-3 border-t border-cyan-200/60 space-y-1 text-[12px] text-slate-700"
                                  data-testid="booking-posted-kit-total-preview"
                                >
                                  <div className="text-[11px] uppercase tracking-wider font-bold text-slate-700 mb-1">
                                    Your booking total
                                  </div>
                                  <div className="flex justify-between">
                                    <span>{test?.name || "Selected test"}</span>
                                    <span>${(subtotal || onlinePrice || 0).toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>
                                      {localLab.enabled
                                        ? "Sample kit + postage both ways"
                                        : "Posted sample kit + prepaid return label"}
                                    </span>
                                    <span data-testid="booking-posted-kit-fee-line">
                                      {localLab.enabled
                                        ? "Included"
                                        : `$${((postedKit.fee_cents || 0) / 100).toFixed(2)}`}
                                    </span>
                                  </div>
                                  <div className="flex justify-between font-bold text-[13px] pt-1 border-t border-cyan-200/60 mt-1">
                                    <span>Total (before any discounts)</span>
                                    <span data-testid="booking-posted-kit-total">
                                      ${((subtotal || onlinePrice || 0) + (localLab.enabled ? 0 : ((postedKit.fee_cents || 0) / 100))).toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="text-[10px] text-slate-500 pt-1">
                                    {localLab.enabled
                                      ? "Postage to you and back to the lab is included in your package price — no separate postage charge at checkout."
                                      : "Postage fee is charged as a separate line item on Stripe checkout — never bundled into the test price."}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          );
                          })()}

                          {/* Phase 2L-A39 — Removed customer-facing "Reason for
                              Testing" and "Primary Water Use" dropdowns from the
                              booking form. The backend `CustomerDetails` schema
                              keeps both fields as Optional, so legacy records and
                              admin views are unaffected. New bookings simply
                              submit empty values for these two fields. */}
                        </div>

                        {/* Property & Water System Information */}
                        <div className="mt-8 pt-7 border-t border-slate-200">
                          <h3 className="font-display text-lg lg:text-xl font-bold text-slate-900">Property & Water System Information</h3>
                          <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                            This information helps us provide more accurate recommendations and build a complete AWARE Water Health Record for your property.
                          </p>
                          <div className="mt-5">
                            <PropertyIntakeForm value={intake} onChange={setIntake} testId={selectedTest} embedded testIdPrefix="booking-intake" />
                          </div>
                        </div>

                        {/* Aware Feb-2026 — the excluded-suburb
                            "out of area" verdict banner (rose card
                            with a "Contact us about drop-off / courier"
                            CTA linking to a legacy out-of-area URL)
                            was removed. Aware is now national and
                            never blocks or nudges customers based on
                            suburb / postcode. */}
                      </>
                    )}

                    <div className="mt-7 flex justify-between">
                      <Button data-testid={BOOKING.step2Back} onClick={back} variant="outline" size="lg" className="rounded-full">
                        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
                      </Button>
                      <Button data-testid={BOOKING.step2Next} onClick={next} size="lg" className="btn-aqua rounded-full font-semibold">
                        Continue to payment <ArrowRight className="w-4 h-4 ml-1.5" />
                      </Button>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div>
                    <h2 className="font-display text-2xl lg:text-3xl font-bold text-slate-900">Secure payment</h2>
                    <p className="mt-2 text-slate-600">
                      {paymentMethod === "invoice"
                        ? "Confirm the invoice details — we’ll email a branded invoice to your agency immediately."
                        : "You’ll be redirected to Stripe to complete payment safely. We never touch your card details."}
                    </p>

                    {isPropertyManager && (
                      <div
                        data-testid="booking-pm-payment-picker"
                        className="mt-6 rounded-2xl bg-cyan-50 border border-cyan-200 p-5"
                      >
                        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700 mb-2">
                          Property Manager · {pmPartner?.business_name || pmPartner?.email}
                        </div>
                        <div className="font-display font-semibold text-slate-900 text-base mb-3">
                          How would you like to pay?
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <button
                            type="button"
                            data-testid="booking-payment-method-stripe"
                            onClick={() => setPaymentMethod("stripe")}
                            className={`rounded-xl border-2 p-3.5 text-left transition-all ${
                              paymentMethod === "stripe"
                                ? "border-cyan-600 bg-white shadow-sm"
                                : "border-slate-200 bg-white/60 hover:border-cyan-300"
                            }`}
                          >
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <CreditCard className="w-4 h-4 text-cyan-700" /> Pay online
                            </div>
                            <div className="mt-1 text-xs text-slate-500">Card / Apple Pay / Google Pay via Stripe</div>
                          </button>
                          <button
                            type="button"
                            data-testid="booking-payment-method-invoice"
                            onClick={() => setPaymentMethod("invoice")}
                            className={`rounded-xl border-2 p-3.5 text-left transition-all ${
                              paymentMethod === "invoice"
                                ? "border-cyan-600 bg-white shadow-sm"
                                : "border-slate-200 bg-white/60 hover:border-cyan-300"
                            }`}
                          >
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <Lock className="w-4 h-4 text-cyan-700" /> Invoice (account)
                            </div>
                            <div className="mt-1 text-xs text-slate-500">Pay by bank transfer · PO number required</div>
                          </button>
                        </div>
                      </div>
                    )}

                    {paymentMethod === "invoice" && isPropertyManager ? (
                      <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-5">
                        <Label htmlFor="po_number" className="text-xs uppercase tracking-wider font-bold text-slate-500">
                          Purchase Order number <span className="text-rose-600">*</span>
                        </Label>
                        <Input
                          id="po_number"
                          data-testid="booking-po-number"
                          value={purchaseOrderNumber}
                          onChange={(e) => setPurchaseOrderNumber(e.target.value)}
                          placeholder="e.g. PO-2026-0814"
                          className="mt-1.5 h-11 rounded-xl font-mono"
                          maxLength={80}
                          required
                        />
                        <p className="mt-2 text-xs text-slate-500">
                          The PO number will appear on the invoice emailed
                          to <strong>{pmPartner?.email}</strong> and on the
                          internal Aware booking record.
                        </p>
                        <ul className="mt-4 space-y-2 text-sm text-slate-700">
                          <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-cyan-600 flex-shrink-0" /> Invoice emailed immediately to your agency</li>
                          <li
                            data-testid="booking-net30-terms-line"
                            className="flex items-start gap-2"
                          >
                            <Check className="w-4 h-4 mt-0.5 text-cyan-600 flex-shrink-0" />
                            <span>
                              <strong>Payable within 30 days</strong>
                              {(() => {
                                const due = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                                const fmt = due.toLocaleDateString("en-AU", {
                                  day: "2-digit", month: "short", year: "numeric",
                                });
                                return <> · Due <span data-testid="booking-net30-due-preview" className="font-semibold text-slate-900">{fmt}</span></>;
                              })()}
                            </span>
                          </li>
                          <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-cyan-600 flex-shrink-0" /> Pay by bank transfer · payment pending until Aware confirms</li>
                          <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-cyan-600 flex-shrink-0" /> No card or Stripe checkout step</li>
                        </ul>
                      </div>
                    ) : (
                      <div className="mt-7 p-6 rounded-2xl bg-slate-50 border border-slate-100">
                        <div className="flex items-center gap-3 mb-4">
                          <CreditCard className="w-5 h-5 text-cyan-600" />
                          <span className="font-display font-semibold text-slate-900">Stripe Hosted Checkout</span>
                        </div>
                        <ul className="space-y-2 text-sm text-slate-700">
                          <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-cyan-600 flex-shrink-0" /> PCI-compliant payment by Stripe</li>
                          <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-cyan-600 flex-shrink-0" /> Accepts card, Apple Pay & Google Pay</li>
                          <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-cyan-600 flex-shrink-0" /> Booking confirmed on payment success</li>
                        </ul>
                        <div className="mt-5 inline-flex items-center gap-2 text-xs text-slate-500">
                          <Lock className="w-3.5 h-3.5" /> 256-bit SSL · Test mode (use card 4242 4242 4242 4242)
                        </div>
                      </div>
                    )}

                    {/* Phase 25 — Customer Consent Wording.
                        Shown on every Step 3 (payment) regardless of
                        whether the booking is partner-attributed —
                        the wording is calm and operational so it's
                        safe even on direct-Aware bookings. */}
                    <div
                      data-testid="ph25-consent-block"
                      className="mt-6 rounded-2xl bg-sky-50 border border-sky-200 p-4"
                    >
                      <div
                        data-testid="ph25-consent-disclosure-text"
                        className="text-xs text-slate-700 leading-relaxed"
                      >
                        Aware Water Testing performs the water
                        test and issues the Certificate of Analysis. If
                        you book through a Pristine Water Systems
                        partner link, or your property is in a
                        supported partner service area, your booking
                        may be linked to that local PWS partner for
                        sample support, follow-up recommendations and
                        corrective service discussions where
                        appropriate. The local PWS partner is not the
                        laboratory. No service work is carried out
                        without your approval. Recommendations are not
                        quotes.
                      </div>
                      <label className="mt-3 flex items-start gap-2 cursor-pointer">
                        <input
                          data-testid="ph25-consent-checkbox"
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                          checked={partnerSupportConsent}
                          onChange={(e) => setPartnerSupportConsent(e.target.checked)}
                        />
                        <span className="text-xs text-slate-700 leading-snug">
                          I understand and agree to the partner-support
                          arrangement described above. Ticking this is
                          optional — your booking will still proceed if
                          you leave it blank.
                        </span>
                      </label>
                      <div className="mt-2">
                        <a
                          data-testid="ph26-booking-explain-link"
                          href="/partner-network"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-cyan-700 underline decoration-cyan-400 hover:text-cyan-800"
                        >
                          Learn how Aware local partner support works →
                        </a>
                      </div>
                    </div>

                    <div className="mt-7 flex justify-between">
                      <Button data-testid={BOOKING.step3Back} onClick={back} variant="outline" size="lg" className="rounded-full">
                        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
                      </Button>
                      {paymentMethod === "invoice" && isPropertyManager ? (
                        <Button
                          data-testid="booking-submit-invoice"
                          onClick={proceedToInvoice}
                          disabled={submitting}
                          size="lg"
                          className="btn-aqua rounded-full font-semibold"
                        >
                          {submitting ? "Sending invoice…" : `Send invoice $${total.toFixed(2)}`}
                        </Button>
                      ) : (
                        <Button data-testid={BOOKING.payBtn} onClick={proceedToStripe} disabled={submitting} size="lg" className="btn-aqua rounded-full font-semibold">
                          {submitting ? "Redirecting..." : `Pay $${total.toFixed(2)} with Stripe`}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600 mb-4">Order Summary</div>
              <div className="space-y-3 pb-4 border-b border-slate-100">
                <div className="flex justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{test?.name}</div>
                    <div className="text-xs text-slate-500">
                      {isPropertyManager
                        ? "Agency / account pricing"
                        : (partnerNationalActive
                            ? "Partner price (kit + postage included)"
                            : (isMember && memberSavings > 0
                                ? "Member price"
                                : (discountEnabled && discountPct > 0
                                    ? `Online prepaid (save ${discountPct}%)`
                                    : "Standard price")))}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-slate-900">${unitPrice.toFixed(2)}</div>
                    {!isPropertyManager && (discountEnabled ? showStrikethrough : false) && (
                      <div className="text-xs text-slate-400 line-through">${partnerNationalActive ? (activePartnerQuote?.local_retail_baseline ?? test?.retail) : test?.retail}</div>
                    )}
                  </div>
                </div>
                {discount && !isPropertyManager && (
                  <div className="flex justify-between gap-4 pt-2" data-testid="booking-discount-line">
                    <div>
                      <div className="text-sm font-semibold text-emerald-700">Discount ({discount.code})</div>
                      <div className="text-xs text-emerald-600">
                        {discount.type === "percentage" ? `${discount.value}% off` : "Fixed dollar discount"}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-emerald-700">−${Number(discount.amount).toFixed(2)}</div>
                  </div>
                )}
              </div>

              {/* Phase 2L-A3 — Discount code Apply field.
                  Phase 2L-A117 — hidden for Property Managers; PMs
                  book on agency/account pricing with no retail
                  consumer promotions. */}
              {!isPropertyManager && (
              <div className="mt-4 pb-4 border-b border-slate-100" data-testid="booking-discount-section">
                {!discount ? (
                  <>
                    <label className="text-xs font-semibold text-slate-700">Discount code</label>
                    <div className="mt-1.5 flex gap-2">
                      <input
                        data-testid="booking-discount-input"
                        type="text"
                        value={discountInput}
                        onChange={(e) => { setDiscountInput(e.target.value.toUpperCase()); if (discountErr) setDiscountErr(""); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyDiscount(); } }}
                        placeholder="Enter code"
                        className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        disabled={applyingDiscount}
                      />
                      <Button
                        data-testid="booking-discount-apply"
                        onClick={applyDiscount}
                        disabled={applyingDiscount || !discountInput.trim()}
                        size="sm"
                        variant="outline"
                        className="text-sm"
                      >
                        {applyingDiscount ? "…" : "Apply"}
                      </Button>
                    </div>
                    {discountErr && (
                      <p data-testid="booking-discount-error" className="mt-2 text-xs text-rose-600">
                        {discountErr}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <div className="text-xs">
                      <div className="font-semibold text-emerald-800" data-testid="booking-discount-applied-label">
                        Discount code applied.
                      </div>
                      <div className="font-mono text-emerald-700">{discount.code}</div>
                    </div>
                    <button
                      data-testid="booking-discount-remove"
                      onClick={removeDiscount}
                      className="text-xs text-emerald-700 hover:text-emerald-900 underline"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* Feb 2026 — Passport add-on retired. Block permanently
                  hidden because `passportAddon` is now hard-coded false.
                  Left in place (dead code) to avoid churning the
                  surrounding order-summary layout; will be removed in
                  the next passport-cleanup sweep. */}
              {passportAddon && (
                <div
                  data-testid="booking-summary-passport-addon"
                  className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-start gap-4"
                >
                  <div>
                    <div className="text-sm font-semibold text-cyan-800 flex items-center gap-1.5">
                      <Crown className="w-3.5 h-3.5 text-cyan-700" />
                      AWARE Water Health Record add-on
                    </div>
                    <div className="text-xs text-slate-500">50% off — booking-only price</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-slate-900 tabular-nums">${(PASSPORT_ADDON_CENTS / 100).toFixed(2)}</div>
                    <div className="text-xs text-slate-400 line-through tabular-nums">$99.00</div>
                  </div>
                </div>
              )}

              {/* Feb-2026 bugfix — surface the posted-kit postage fee
                  in the sidebar summary so the customer sees the same
                  breakdown that will appear on the Stripe checkout page.
                  Only renders when mail_out was chosen AND the paid
                  posted-kit flag is visible to this caller. */}
              {postedKit.enabled && kitMethod === "mail_out" && (postedKit.fee_cents || 0) > 0 && (
                <div
                  data-testid="booking-summary-posted-kit-line"
                  className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-start gap-4"
                >
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Mail-Out Sample Kit
                    </div>
                    <div className="text-xs text-slate-500">Charged as a separate Stripe line item.</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-slate-900 tabular-nums">
                      ${((postedKit.fee_cents || 0) / 100).toFixed(2)}
                    </div>
                  </div>
                </div>
              )}

              {/* Feb 2026 — All-Inclusive Pricing rollout: $20 local-lab
                  saving line. Renders when the master flag is on, the
                  customer's postcode qualifies, AND they picked one of
                  the two lab-attendance options. Server re-validates
                  the discount on /payments/checkout — this line is
                  purely UX so the sidebar total matches Stripe. */}
              {localLabSavingAmount > 0 && (
                <div
                  data-testid="booking-summary-local-lab-saving-line"
                  className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-start gap-4"
                >
                  <div>
                    <div className="text-sm font-semibold text-emerald-800">
                      Local lab saving
                    </div>
                    <div className="text-xs text-slate-500">
                      {kitMethod === "local_pickup_dropoff"
                        ? "Applied for picking up and returning your kit at the AWARE lab."
                        : "Applied for dropping your sample at the AWARE lab."}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-emerald-700 tabular-nums">
                      −${localLabSavingAmount.toFixed(2)}
                    </div>
                  </div>
                </div>
              )}

              {/* Feb 2026 — Partner-supplied kit sidebar line. Only
                  renders when the customer picked the partner-supplied
                  option AND the server confirmed availability. */}
              {partnerKitSelected && (
                <div
                  data-testid="booking-summary-partner-kit-line"
                  className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-start gap-4"
                >
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {partnerKitOptions.available
                        ? "Already provided by my local PWS Partner"
                        : "Kit already supplied by a PWS Partner"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {(() => {
                        const chargeCents = partnerKitOptions.available
                          ? partnerKitOptions.customer_charge_cents
                          : supplyingPartners.chargeCents;
                        const name = partnerKitOptions.partner?.business_name
                          || (supplyingPartners.rows.find(p => p.id === selectedSupplyingPartnerId) || {}).business_name;
                        if (chargeCents > 0) {
                          return name
                            ? `From ${name} · charged as a separate Stripe line item.`
                            : "Charged as a separate Stripe line item.";
                        }
                        return name
                          ? `Provided at no charge by ${name}.`
                          : "Provided at no charge by your local PWS Partner.";
                      })()}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-slate-900 tabular-nums">
                      {partnerKitChargeAmount > 0
                        ? `$${partnerKitChargeAmount.toFixed(2)}`
                        : "Free"}
                    </div>
                  </div>
                </div>
              )}

              {/* Jun 2026 — Water Health member discount line. Server
                  re-validates + applies on /payments/checkout; this line
                  keeps the sidebar total honest with Stripe. */}
              {lwhDiscountAmount > 0 && (
                <div
                  data-testid="booking-summary-lwh-discount-line"
                  className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-start gap-4"
                >
                  <div>
                    <div className="text-sm font-semibold text-emerald-800">
                      Water Health member discount ({lwhDiscount.pct}%)
                    </div>
                    <div className="text-xs text-slate-500">
                      Applied because this property has an active Water Health Program membership.
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-emerald-700 tabular-nums">
                      −${lwhDiscountAmount.toFixed(2)}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 flex justify-between items-baseline">
                <span className="text-sm font-semibold text-slate-700">Total</span>
                <span data-testid="booking-summary-total" className="font-display text-3xl font-bold text-slate-900">${total.toFixed(2)} <span className="text-sm font-medium text-slate-500">AUD</span></span>
              </div>
              {/* Phase 9 — Partner national pricing note. Appears only
                  when the booking is attributed to an active PWS
                  franchisee. Calm, single-line wording per spec. */}
              {partnerNationalActive && (
                <div
                  data-testid="booking-summary-partner-pricing-note"
                  className="mt-4 p-3 rounded-xl bg-cyan-50 border border-cyan-200"
                >
                  <p className="text-xs text-cyan-900 flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" />
                    <span className="font-semibold">{(activePartnerQuote && activePartnerQuote.kit_postage_blurb) || "Includes sample kit and return postage allowance."}</span>
                  </p>
                  {(activePartnerQuote && activePartnerQuote.partner && activePartnerQuote.partner.business_name) && (
                    <p className="mt-1 text-[11px] text-cyan-800/80" data-testid="booking-summary-partner-name">
                      Booked via {activePartnerQuote.partner.business_name}
                    </p>
                  )}
                </div>
              )}
              {isMember ? (
                <div data-testid="booking-summary-member-savings" className="mt-4 p-3 rounded-xl bg-cyan-50 border border-cyan-200">
                  <p className="text-xs text-cyan-800 flex items-center gap-1.5">
                    <Crown className="w-3.5 h-3.5" />
                    <span className="font-bold">You saved ${onlineSavings.toFixed(0)}</span> vs the ${test?.retail} retail price.
                  </p>
                </div>
              ) : isPropertyManager ? (
                /* Phase 2L-A117 — PMs see agency/account pricing only;
                   the retail "save 20% online prepaid" promo and the
                   Passport upsell are hidden. */
                <div data-testid="booking-summary-pm-context" className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-xs text-slate-700 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-cyan-700" />
                    <span className="font-bold">Agency / account pricing</span>
                    <span className="text-slate-500">· billed to your agency</span>
                  </p>
                </div>
              ) : partnerNationalActive ? null : (
                (discountEnabled && discountPct > 0 && onlineSavings > 0) ? (
                  <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                    <p data-testid="booking-summary-online-savings" className="text-xs text-emerald-800 flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5" />
                      <span className="font-bold">You&apos;re saving ${onlineSavings.toFixed(0)}</span> with online prepaid ({discountPct}% off).
                    </p>
                    {/* Feb 2026 — AWARE Water Health Record upsell removed. The
                        free AWARE Water Health Record is now surfaced as
                        an "Included at no extra cost" panel on step 1. */}
                  </div>
                ) : null
              )}
              {/* Phase 2L-A3 — Included with your water test */}
              <div className="mt-5 pt-5 border-t border-slate-100" data-testid="booking-included-section">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700 mb-3">
                  Included with your water test
                </div>
                <ul className="space-y-3 text-xs text-slate-700">
                  <li className="flex items-start gap-2" data-testid="booking-included-summary">
                    <Check className="w-3.5 h-3.5 mt-0.5 text-cyan-600 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-slate-900">Plain-English Results Summary</div>
                      <div className="text-slate-600">Easy-to-read explanation of your water test results.</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-2" data-testid="booking-included-action-plan">
                    <Check className="w-3.5 h-3.5 mt-0.5 text-cyan-600 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-slate-900">Water Test Action Plan</div>
                      <div className="text-slate-600">Clear next steps based on the issues identified in your results.</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-2" data-testid="booking-included-recommendations">
                    <Check className="w-3.5 h-3.5 mt-0.5 text-cyan-600 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-slate-900">Treatment &amp; Correction Recommendations</div>
                      <div className="text-slate-600">Practical recommendations where supported by the test results.</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-2" data-testid="booking-included-proposal">
                    <Check className="w-3.5 h-3.5 mt-0.5 text-cyan-600 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-slate-900">Corrective Estimate Option</div>
                      <div className="text-slate-600">Option to request an estimate for supply and installation of recommended products and services.</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-2" data-testid="booking-included-coa">
                    <Check className="w-3.5 h-3.5 mt-0.5 text-cyan-600 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-slate-900">Certificate of Analysis</div>
                      <div className="text-slate-600">Formal laboratory report showing tested parameters, results, ADWG references, and methods.</div>
                    </div>
                  </li>
                </ul>
                <div className="mt-3 text-[11px] text-slate-500 italic">Included at no extra charge.</div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
